import React, { useMemo, useRef, useState } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";
import { getCenikItem } from "../../data/cenikUtils";
import {
  getStaffLabel,
  getSpeakerLabel,
  getMusicTypeLabel,
  getMusicPersonLabel,
  getVehicleLabelById,
} from "../../utils/calendarRoles";
import html2pdf from "html2pdf.js";

function hasRecContent(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "boolean") return value === true;
  if (Array.isArray(value)) return value.some(hasRecContent);
  if (typeof value === "object") return Object.values(value).some(hasRecContent);
  return false;
}

function getRecStatusKey(rec) {
  const stored = String(rec?.stav || "").trim();
  if (stored) return stored;

  const hasDraft = hasRecContent(rec?.text?.navrhReci);
  const hasNotes = hasRecContent(rec?.text?.poznamky);
  const hasFamily = hasRecContent(rec?.rodina);
  const hasLife = hasRecContent(rec?.zivot);
  const hasCharacter = hasRecContent(rec?.charakter);
  const hasSensitive = hasRecContent(rec?.citlive);
  const hasForm = hasRecContent(rec?.forma);

  if (hasDraft) return "k_revizi";
  if (hasNotes || hasFamily || hasLife || hasCharacter || hasSensitive || hasForm) return "rozpracovano";
  return "bez_podkladu";
}

function getRecStatusLabel(recOrStatus) {
  const status = typeof recOrStatus === "string" ? recOrStatus : getRecStatusKey(recOrStatus);

  if (status === "hotovo") return "Hotová";
  if (status === "k_revizi") return "K revizi";
  if (status === "rozpracovano") return "Rozpracovaná";
  return "Bez podkladů";
}

function getRecCardMeta(status) {
  if (status === "hotovo") {
    return {
      border: "1px solid #86efac",
      background: "#f0fdf4",
      color: "#166534",
    };
  }
  if (status === "k_revizi") {
    return {
      border: "1px solid #93c5fd",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }
  if (status === "rozpracovano") {
    return {
      border: "1px solid #fdba74",
      background: "#fff7ed",
      color: "#9a3412",
    };
  }
  return {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
  };
}

function getRecStatusColor(status) {
  if (status === "hotovo") {
    return {
      border: "1px solid #86efac",
      background: "#f0fdf4",
      color: "#166534",
    };
  }
  if (status === "k_revizi") {
    return {
      border: "1px solid #93c5fd",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }
  if (status === "rozpracovano") {
    return {
      border: "1px solid #fdba74",
      background: "#fff7ed",
      color: "#9a3412",
    };
  }
  return {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
  };
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function getPrevozCilTypLabel(value) {
  const key = String(value || "").trim();

  if (key === "soudni_pitva") return "Soudní pitva / patologie";
  if (key === "zdravotni_pitva") return "Zdravotní pitva / patologie";
  if (key === "pitva_patologie") return "Pitva / patologie";
  if (key === "krematorium") return "Krematorium";
  if (key === "hrbitov") return "Hřbitov";
  if (!key) return "-";
  return key;
}

function formatMoney(n) {
  return `${round2(n).toFixed(2).replace(".", ",")} Kč`;
}

function getRolePerson(roleAssignments = [], role) {
  const arr = Array.isArray(roleAssignments) ? roleAssignments : [];
  const item = arr.find((x) => String(x?.role || "").trim() === String(role || "").trim());
  return String(item?.personLabel || item?.personId || "").trim();
}

function getVehicleLabel(vehicleAssignments = [], role = "") {
  const arr = Array.isArray(vehicleAssignments) ? vehicleAssignments : [];
  const item = role
    ? arr.find((x) => String(x?.role || "").trim() === String(role || "").trim())
    : arr[0];

  return String(item?.vehicleLabel || item?.vehicleId || "").trim();
}

function renderSummaryValue(value) {
  return String(value || "").trim() || "—";
}

function renderStaffSummary(value) {
  const v = String(value || "").trim();
  return v ? getStaffLabel(v) : "—";
}

function renderSpeakerSummary(value) {
  const v = String(value || "").trim();
  return v ? getSpeakerLabel(v) : "—";
}

function renderMusicTypeSummary(value) {
  const v = String(value || "").trim();
  return v ? getMusicTypeLabel(v) : "—";
}

function renderMusicPersonSummary(value) {
  const v = String(value || "").trim();
  return v ? getMusicPersonLabel(v) : "—";
}

function renderVehicleSummary(value) {
  const v = String(value || "").trim();
  return v ? getVehicleLabelById(v) : "—";
}

function getDopravaModeLabel(mode) {
  const m = String(mode || "").trim();
  if (m === "km") return "Skutečné km";
  if (m === "pausal_ck") return "Paušál po ČK";
  if (m === "pausal_cb") return "Paušál krematorium ČB";
  if (m === "combo_cb") return "Etapa 1 v km + paušál ČB";
  return m || "-";
}

function getDopravaSummary(mode, kmCelkem) {
  const m = String(mode || "").trim();
  const km = Number(kmCelkem || 0) || 0;

  if (m === "km") {
    return km > 0
      ? `Doprava účtována dle skutečných km: ${km} km`
      : "Doprava účtována dle skutečných km";
  }

  if (m === "pausal_ck") {
    return "Doprava účtována paušálem po Českém Krumlově";
  }

  if (m === "pausal_cb") {
    return "Doprava účtována paušálem do krematoria České Budějovice";
  }

  if (m === "combo_cb") {
    return km > 0
      ? `Doprava účtována kombinovaně: etapa 1 = ${km} km + paušál do krematoria České Budějovice`
      : "Doprava účtována kombinovaně: etapa 1 v km + paušál do krematoria České Budějovice";
  }

  return "";
}

const FINAL_SECTION_LABELS = {
  ostatni: "Ostatní",
  prevoz: "Převoz",
  rakev: "Rakev a úprava zesnulého",
  obrad: "Obřad",
  urna: "Urna",
  urna_navazujici: "Urna navazující",
  kvetiny: "Květiny",
  media: "Média",
};

const FINAL_SUBGROUP_LABELS = {
  ostatni: {
    "": "Bez podboxu",
  },
  prevoz: {
    "": "Bez podboxu",
    doprava: "Doprava",
    chlazeni_mrazeni: "Chlazení a mražení",
    manipulace: "Manipulace",
  },
  rakev: {
    "": "Bez podboxu",
    rakev_priprava: "Rakev a příprava",
    uprava_zesnuleho: "Úprava zesnulého",
  },
  obrad: {
    "": "Bez podboxu",
    hudba_recnik: "Hudba a řečník",
    misto_a_vybava: "Místo a vybavení",
  },
  urna: {
    "": "Bez podboxu",
    urna_typ: "Typ urny",
    urna_ukony: "Úkony s urnou",
  },
  urna_navazujici: {
    "": "Bez podboxu",
    urna_typ: "Typ urny",
    urna_ukony: "Úkony s urnou",
  },
  kvetiny: {
    "": "Bez podboxu",
    zakladni_kvetiny: "Květiny a vazba",
    vystaveni_kvetin: "Vystavení a aranžmá",
  },
  media: {
    "": "Bez podboxu",
    foto_video: "Foto / video / záznam",
    projekce: "Projekce a přenos",
  },
};

function getFinalSectionLabel(sectionKey) {
  return FINAL_SECTION_LABELS[String(sectionKey || "ostatni")] || "Ostatní";
}

function getFinalSubgroupLabel(sectionKey, subgroupKey) {
  const sec = String(sectionKey || "ostatni");
  const sub = String(subgroupKey || "");
  const map = FINAL_SUBGROUP_LABELS[sec] || FINAL_SUBGROUP_LABELS.ostatni || {};
  return map[sub] || "Bez podboxu";
}

function calcServiceRow(cenaBezDph, dphPct, mnozstvi) {
  const bez = round2((Number(cenaBezDph) || 0) * (Number(mnozstvi) || 0));
  const dph = round2((bez * (Number(dphPct) || 0)) / 100);
  return { bez, dph, celkem: round2(bez + dph) };
}

function isSuspiciousPrice(value) {
  const n = Number(value);
  return !Number.isFinite(n) || n <= 0;
}

function getFamilyWorkflowMeta(status) {
  if (status === "uzamceno") {
    return {
      label: "Uzamčeno",
      border: "#fcd34d",
      background: "#fffbeb",
      color: "#92400e",
    };
  }
  if (status === "potvrzeno_rodinou") {
    return {
      label: "Potvrzeno rodinou",
      border: "#86efac",
      background: "#f0fdf4",
      color: "#166534",
    };
  }
  if (status === "pripraveno_k_odeslani") {
    return {
      label: "Připraveno k odeslání",
      border: "#93c5fd",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }
  return {
    label: "Neodesláno",
    border: "#d1d5db",
    background: "#f3f4f6",
    color: "#6b7280",
  };
}

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 24,
  padding: 24,
  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 18,
  background: "#f9fafb",
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 16,
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 900,
  color: "#111827",
  marginBottom: 12,
};

const rowStyle = {
  display: "grid",
  gridTemplateColumns: "150px 1fr",
  gap: 10,
  padding: "7px 0",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
};

const labelStyle = {
  fontWeight: 800,
  color: "#374151",
};

const valueStyle = {
  color: "#111827",
  wordBreak: "break-word",
};

const buttonPrimary = {
  padding: "12px 16px",
  borderRadius: 14,
  border: "1px solid #111827",
  background: "#111827",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
};

const buttonSecondary = {
  padding: "12px 16px",
  borderRadius: 14,
  border: "1px solid #d1d5db",
  background: "#fff",
  color: "#111827",
  fontWeight: 800,
  cursor: "pointer",
};

const topActionsWrapStyle = {
  marginTop: 12,
  display: "flex",
  gap: 10,
  justifyContent: "flex-end",
  flexWrap: "wrap",
};

const topActionButtonStyle = {
  minWidth: 160,
};

const mobileStackStyle = {
  display: "grid",
  gap: 12,
};

const mobileCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  background: "#fff",
  padding: 14,
};

const mobileLabelStyle = {
  fontSize: 12,
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.03em",
  marginBottom: 6,
};

const mobileValueStyle = {
  fontSize: 15,
  color: "#111827",
  fontWeight: 700,
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  background: "#eef2ff",
  color: "#3730a3",
  fontWeight: 800,
  fontSize: 14,
};

const heroStyle = {
  marginBottom: 18,
  padding: 18,
  borderRadius: 18,
  background: "#111827",
  color: "#fff",
};

const badgeRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginTop: 14,
  alignItems: "stretch",
};

const softBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 999,
  background: "#f3f4f6",
  color: "#111827",
  fontWeight: 800,
  fontSize: 13,
  border: "1px solid #e5e7eb",
};

const printSheetStyle = {
  border: "2px solid #111827",
  borderRadius: 12,
  background: "#fff",
  padding: 14,
  marginBottom: 18,
  overflowX: "auto",
};

const printBrandWrapStyle = {
  marginBottom: 12,
  paddingBottom: 10,
  borderBottom: "1px solid #111827",
};

const printBrandImageStyle = {
  display: "block",
  width: "100%",
  maxWidth: 720,
  height: "auto",
  objectFit: "contain",
};

const printTopGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 0,
  border: "1px solid #111827",
  marginTop: 12,
};

const printCellStyle = {
  borderRight: "1px solid #111827",
  borderBottom: "1px solid #111827",
  padding: 10,
  minHeight: 110,
};

const printTableWrapStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 0,
  borderLeft: "1px solid #111827",
  borderRight: "1px solid #111827",
  borderBottom: "1px solid #111827",
};

const printTableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
};

const printThStyle = {
  border: "1px solid #111827",
  padding: "6px 8px",
  textAlign: "left",
  fontWeight: 800,
  background: "#f3f4f6",
};

const printTdStyle = {
  border: "1px solid #111827",
  padding: "6px 8px",
  verticalAlign: "top",
};

const printTotalsStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 320px",
  gap: 16,
  marginTop: 12,
  alignItems: "start",
};


const statusGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginBottom: 18,
};

const statusCardStyle = (ok) => ({
  border: ok ? "1px solid #bbf7d0" : "1px solid #fecaca",
  background: ok ? "#f0fdf4" : "#fef2f2",
  color: ok ? "#166534" : "#991b1b",
  borderRadius: 16,
  padding: 14,
});

const infoBoxStyle = {
  marginBottom: 18,
  padding: 14,
  borderRadius: 16,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
};

const financeGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: 12,
  marginBottom: 18,
};

const financeCardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  padding: 14,
  background: "#fff",
};

const financeLabelStyle = {
  fontSize: 12,
  fontWeight: 800,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const financeValueStyle = {
  marginTop: 6,
  fontSize: 22,
  fontWeight: 900,
  color: "#111827",
};

const financeStateStyle = (stav) => ({
  border: stav === "Uhrazeno" || stav === "Přeplaceno"
    ? "1px solid #bbf7d0"
    : stav === "Částečně uhrazeno"
      ? "1px solid #fde68a"
      : "1px solid #fecaca",
  background: stav === "Uhrazeno" || stav === "Přeplaceno"
    ? "#f0fdf4"
    : stav === "Částečně uhrazeno"
      ? "#fffbeb"
      : "#fef2f2",
  color: stav === "Uhrazeno" || stav === "Přeplaceno"
    ? "#166534"
    : stav === "Částečně uhrazeno"
      ? "#92400e"
      : "#991b1b",
  borderRadius: 14,
  padding: 14,
});

function hasAnyFilled(obj) {
  if (!obj || typeof obj !== "object") return false;
  return Object.values(obj).some((value) => String(value || "").trim() !== "");
}

function isFilled(value) {
  return String(value || "").trim() !== "";
}

function Row({ label, value }) {
  return (
    <div style={rowStyle}>
      <div style={labelStyle}>{label}</div>
      <div style={valueStyle}>{String(value || "-")}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={sectionStyle}>
      <div style={titleStyle}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

function FilledRow({ label, value }) {
  if (!isFilled(value)) return null;
  return <Row label={label} value={value} />;
}

function readToken() {
  try {
    return (
      localStorage.getItem("pohrebka_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function apiUrl(path) {
  const base = String(import.meta?.env?.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  return path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
}

function getFilenameFromDisposition(headerValue, fallback = "dokumenty.zip") {
  const raw = String(headerValue || "");
  const utf = raw.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1]);
    } catch {
      return utf[1];
    }
  }
  const simple = raw.match(/filename="?([^"]+)"?/i);
  return simple?.[1] || fallback;
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "dokumenty.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function Pohrebka2FinalForm() {
  const { order, currentOrderId, saveOrder, saving } = usePohrebka2();

  const zakladSummary = order?.zaklad || {};
  const obradSummary = order?.obrad || {};
  const prevozSummary = order?.prevoz || {};
  const vstupniInformaceSummary = order?.vstupniInformace || {};
  const zesnulaOsobaSummary = order?.zesnulaOsoba || {};
  const vypravitelSummary = order?.vypravitel || {};

  const obradRolesSummary = Array.isArray(obradSummary?.roleAssignments) ? obradSummary.roleAssignments : [];
  const obradVehiclesSummary = Array.isArray(obradSummary?.vehicleAssignments) ? obradSummary.vehicleAssignments : [];
  const prevozRolesSummary = Array.isArray(prevozSummary?.roleAssignments) ? prevozSummary.roleAssignments : [];
  const prevozVehiclesSummary = Array.isArray(prevozSummary?.vehicleAssignments) ? prevozSummary.vehicleAssignments : [];

  const vedouciObradu = getRolePerson(obradRolesSummary, "vedouci_obradu");
  const obradniSluzba = getRolePerson(obradRolesSummary, "obradni_sluzba");
  const recnikOsoba = getRolePerson(obradRolesSummary, "recnik");
  const hudbaOsoba = getRolePerson(obradRolesSummary, "hudba");

  const hlavniVuz = getVehicleLabel(obradVehiclesSummary, "hlavni_vuz");
  const prevozoveAuto = getVehicleLabel(prevozVehiclesSummary, "prevozove_auto");

  const ridicPrevozu = getRolePerson(prevozRolesSummary, "ridic_prevozu");
  const doprovodPrevozu = getRolePerson(prevozRolesSummary, "doprovod_prevozu");

  const zesnulyJmeno = [
    vstupniInformaceSummary?.jmenoZesnuleho || zesnulaOsobaSummary?.jmeno || "",
    vstupniInformaceSummary?.prijmeniZesnuleho || zesnulaOsobaSummary?.prijmeni || "",
  ].filter(Boolean).join(" ").trim();

  const objednavatelJmeno = [
    vypravitelSummary?.jmeno || "",
    vypravitelSummary?.prijmeni || "",
  ].filter(Boolean).join(" ").trim();

    const resolveCenikItem = (id) => {
      const base = getCenikItem(id);
      const override = order?.cenikOverrides?.[id] || {};

      if (!base && (!override || typeof override !== "object" || override.__custom !== true)) {
        return null;
      }

      return {
        ...(base || { id, defaultQty: 1, kategorie: "custom" }),
        ...override,
        id,
        nazev:
          override?.nazev !== undefined && override?.nazev !== ""
            ? override.nazev
            : (base?.nazev || id),
        cenaBezDph:
          override?.cenaBezDph !== undefined && override?.cenaBezDph !== ""
            ? Number(override.cenaBezDph) || 0
            : Number(base?.cenaBezDph) || 0,
        dph:
          override?.dph !== undefined && override?.dph !== ""
            ? Number(override.dph) || 0
            : Number(base?.dph) || 0,
        jednotka:
          override?.jednotka !== undefined && override?.jednotka !== ""
            ? override.jednotka
            : (base?.jednotka || "ks"),
        sekceKey:
          override?.sekceKey !== undefined && override?.sekceKey !== ""
            ? override.sekceKey
            : (base?.sekceKey || "ostatni"),
        subgroupKey:
          override?.subgroupKey !== undefined
            ? override.subgroupKey
            : (base?.subgroupKey || ""),
      };
    };

  const rec = order?.rec || {};
  const recStatusKey = getRecStatusKey(rec);
  const recStatus = getRecStatusLabel(recStatusKey);
  const recStatusStyle = getRecStatusColor(recStatusKey);
  const recDraft = String(rec?.text?.navrhReci || "").trim();
  const recNotes = String(rec?.text?.poznamky || "").trim();
  const recHasFamily = hasRecContent(rec?.rodina);
  const recHasLife = hasRecContent(rec?.zivot);
  const recHasCharacter = hasRecContent(rec?.charakter);
  const recHasSensitive = hasRecContent(rec?.citlive);
  const recHasForm = hasRecContent(rec?.forma);
  const recHasHudba =
    hasRecContent(rec?.hudba) ||
    (Array.isArray(rec?.hudba?.dalsiSeznam) && rec.hudba.dalsiSeznam.some((x) => String(x || "").trim()));
  const recHudbaDalsiList = Array.isArray(rec?.hudba?.dalsiSeznam)
    ? rec.hudba.dalsiSeznam.filter((x) => String(x || "").trim())
    : (String(rec?.hudba?.dalsi || "").trim() ? [String(rec.hudba.dalsi).trim()] : []);
  const familyWorkflowStatus = rec?.stavRodina || "neodeslano";
  const familyWorkflowMeta = getFamilyWorkflowMeta(familyWorkflowStatus);
  const familyConfirmed = rec?.potvrzenoRodinou === true;
  const familyLocked = rec?.uzamcenoPoPotvrzeni === true;
  const [generating, setGenerating] = useState(false);
  const [generateMsg, setGenerateMsg] = useState("");
  const [recExpanded, setRecExpanded] = useState(false);
  const printAreaRef = useRef(null);

  const handlePrintFinal = () => {
    const node = printAreaRef.current;
    if (!node) return;

    const win = window.open("", "_blank", "width=1200,height=900");
    if (!win) return;

    win.document.open();
    win.document.write(`<!doctype html>
<html lang="cs">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Tisk objednávky</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; padding: 24px; background: #fff; color: #111; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #111; padding: 6px 8px; vertical-align: top; }
    img { max-width: 100%; height: auto; }
    @page { size: A4; margin: 12mm; }
  </style>
</head>
<body>${node.innerHTML}</body>
</html>`);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
    }, 300);
  };

  const handleExportPdf = async () => {
    const node = printAreaRef.current;
    if (!node) return;

    const filename = `${String(currentOrderId || "objednavka")}.pdf`;

    await html2pdf()
      .set({
        margin: 8,
        filename,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .from(node)
      .save();
  };

  const recCardMeta = getRecCardMeta(recStatusKey);

  const zaklad = order?.zaklad || {};
  const vstupni = order?.vstupniInformace || {};
  const vypravitel = order?.vypravitel || {};
  const zesnula = order?.zesnulaOsoba || {};
  const prevoz = order?.prevoz || {};
  const vyjezd = order?.vyjezd || {};

  const vyjezdPlatceKey = String(zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "").trim();
  const jeVyjezdPcr = vyjezdPlatceKey === "pcr";
  const jeVyjezdPojistovna = vyjezdPlatceKey === "pojistovna";
  const jeVyjezdRodina = vyjezdPlatceKey === "rodina";
  const maNavazujiciRodinu = !!prevoz?.navazujiciRodinnyPrevoz || jeVyjezdRodina;
  const hlavniVyjezdVetevLabel =
    jeVyjezdPcr ? "Policie ČR" :
    jeVyjezdPojistovna ? "Zdravotní pojišťovna" :
    jeVyjezdRodina ? "Rodina" :
    "Neuvedeno";

  const activeFinanceCardStyle = {
    padding: 14,
    borderRadius: 14,
    background: "#ffffff",
    border: "2px solid #bbf7d0",
    boxShadow: "0 8px 24px rgba(34,197,94,0.10)",
  };

  const inactiveFinanceCardStyle = {
    padding: 14,
    borderRadius: 14,
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    opacity: 0.72,
  };

function normalizeDateOnly(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
  }
  return "";
}

function computeInvoiceVisualStatus(faktura = {}) {
  const rawStav = String(faktura?.stav || "").trim().toLowerCase();
  const datumSplatnosti = normalizeDateOnly(faktura?.datumSplatnosti || faktura?.splatnost);
  const odeslanoDne = String(faktura?.odeslanoDne || "").trim();
  const today = new Date().toISOString().slice(0, 10);

  if (["uhrazeno", "zaplaceno", "proplaceno", "uhrazená", "uhrazeno_cele"].includes(rawStav)) {
    return {
      label: "Uhrazeno",
      background: "#dcfce7",
      color: "#166534",
      border: "1px solid #86efac",
    };
  }

  if (datumSplatnosti && datumSplatnosti < today && rawStav !== "uhrazeno") {
    return {
      label: "Po splatnosti",
      background: "#fee2e2",
      color: "#991b1b",
      border: "1px solid #fca5a5",
    };
  }

  if (odeslanoDne || ["odeslano", "vystaveno", "cekame_na_uhradu", "čekáme_na_úhradu"].includes(rawStav)) {
    return {
      label: "Odesláno",
      background: "#dbeafe",
      color: "#1d4ed8",
      border: "1px solid #93c5fd",
    };
  }

  return {
    label: "Neodesláno",
    background: "#f3f4f6",
    color: "#4b5563",
    border: "1px solid #d1d5db",
  };
}

function invoiceStatusBadgeStyle(status) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: status?.background || "#f3f4f6",
    color: status?.color || "#4b5563",
    border: status?.border || "1px solid #d1d5db",
    width: "fit-content",
  };
}


  const financeCardTitleStyle = (active) => ({
    fontWeight: 800,
    marginBottom: 8,
    color: active ? "#166534" : "#111827",
  });

  const financeBadge = (active, label) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 8px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 800,
        marginLeft: 8,
        background: active ? "#dcfce7" : "#f3f4f6",
        color: active ? "#166534" : "#6b7280",
        border: active ? "1px solid #bbf7d0" : "1px solid #e5e7eb",
      }}
    >
      {label}
    </span>
  );

  const aktivniPcrVetev =
    typeof prevoz?.fakturaceRozpad?.pcr?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pcr.aktivni
      : jeVyjezdPcr;

  const aktivniPojistovnaVetev =
    typeof prevoz?.fakturaceRozpad?.pojistovna?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.pojistovna.aktivni
      : jeVyjezdPojistovna;

  const aktivniRodinnaVetev =
    typeof prevoz?.fakturaceRozpad?.rodina?.aktivni === "boolean"
      ? prevoz.fakturaceRozpad.rodina.aktivni
      : maNavazujiciRodinu;

  const hlavniFakturacniVetevLabel =
    aktivniPcrVetev ? "Policie ČR" :
    aktivniPojistovnaVetev ? "Zdravotní pojišťovna" :
    aktivniRodinnaVetev ? "Rodina" :
    "-";

  const maVedlejsiFinanceVetev =
    (!aktivniPcrVetev) || (!aktivniPojistovnaVetev) || (!aktivniRodinnaVetev);

  const pcrVisualStatus = computeInvoiceVisualStatus(
    prevoz?.externiFakturace?.pcr || vyjezd?.externiFakturace?.pcr || {}
  );

  const pojistovnaVisualStatus = computeInvoiceVisualStatus(
    prevoz?.externiFakturace?.pojistovna || vyjezd?.externiFakturace?.pojistovna || {}
  );

  const rodinaVisualStatus = computeInvoiceVisualStatus(
    prevoz?.rodinnaFakturace || {}
  );

  const vyjezdRozpadBox = (
    <div
      style={{
        marginBottom: 18,
        padding: 16,
        borderRadius: 18,
        border: "1px solid #e5e7eb",
        background: "#f9fafb",
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: "#111827",
          marginBottom: 10,
        }}
      >
        Výjezdová část zakázky
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
          marginBottom: 14,
        }}
      >
        <div><strong>Režim výjezdu:</strong> {zaklad?.vyjezdRezim || vyjezd?.vyjezdRezim || "-"}</div>
        <div><strong>Primární plátce výjezdu:</strong> {zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce || "-"}</div>
        <div><strong>Pojišťovna:</strong> {zaklad?.zdravotniPojistovnaNazev || vyjezd?.zdravotniPojistovnaNazev || "-"}</div>
        <div><strong>Navazující rodinný převoz:</strong> {prevoz?.navazujiciRodinnyPrevoz ? "ANO" : "NE"}</div>
        <div><strong>Pitva uzavřena:</strong> {prevoz?.pitvaUzavrena ? "ANO" : "NE"}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        {aktivniPcrVetev ? (
          <div style={activeFinanceCardStyle}>
            <div style={financeCardTitleStyle(true)}>
              Výjezd – Policie ČR
              {financeBadge(true, "hlavní větev")}
            </div>
            <div><strong>Aktivní:</strong> ANO</div>
            <div><strong>Km:</strong> {prevoz?.kmRozpad?.pcrKm || "0"}</div>
            <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pcr?.cislo || vyjezd?.externiFakturace?.pcr?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pcr?.datum || vyjezd?.externiFakturace?.pcr?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pcr?.subjekt || vyjezd?.externiFakturace?.pcr?.subjekt || "Policie ČR"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pcr?.castkaBezDph || "-"}</div>
          </div>
        ) : null}

        {aktivniPojistovnaVetev ? (
          <div style={activeFinanceCardStyle}>
            <div style={financeCardTitleStyle(true)}>
              Výjezd – Zdravotní pojišťovna
              {financeBadge(true, "hlavní větev")}
            </div>
            <div><strong>Aktivní:</strong> ANO</div>
            <div><strong>Km:</strong> {prevoz?.kmRozpad?.pojistovnaKm || "0"}</div>
            <div><strong>Výpočet:</strong> {prevoz?.kmRozpad?.pojistovnaKmVypocet || "-"}</div>
            <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pojistovna?.cislo || vyjezd?.externiFakturace?.pojistovna?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pojistovna?.datum || vyjezd?.externiFakturace?.pojistovna?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || vyjezd?.externiFakturace?.pojistovna?.subjekt || "-"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pojistovna?.castkaBezDph || "-"}</div>
          </div>
        ) : null}

        {aktivniRodinnaVetev ? (
          <div style={activeFinanceCardStyle}>
            <div style={financeCardTitleStyle(true)}>
              Rodina – navazující zakázka
              {financeBadge(true, "navazuje")}
            </div>
            <div><strong>Aktivní:</strong> ANO</div>
            <div><strong>Km:</strong> {prevoz?.kmRozpad?.rodinaKm || prevoz?.rodinnaFakturace?.km || "0"}</div>
            <div><strong>Číslo:</strong> {prevoz?.rodinnaFakturace?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.rodinnaFakturace?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.rodinnaFakturace?.subjekt || "Rodina"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.rodinnaFakturace?.castkaBezDph || "-"}</div>
            <div><strong>Poznámka:</strong> {prevoz?.rodinnaFakturace?.poznamka || "-"}</div>
          </div>
        ) : null}
      </div>

      {maVedlejsiFinanceVetev ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#6b7280", marginBottom: 8 }}>
            Neaktivní varianty výjezdu
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
            {!aktivniPcrVetev ? (
              <div style={inactiveFinanceCardStyle}>
                <div style={financeCardTitleStyle(false)}>
                  Výjezd – Policie ČR
                  {financeBadge(false, "neaktivní")}
                </div>
                <div><strong>Aktivní:</strong> NE</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.pcrKm || "0"}</div>
                <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pcr?.cislo || vyjezd?.externiFakturace?.pcr?.cislo || "-"}</div>
                <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pcr?.datum || vyjezd?.externiFakturace?.pcr?.datum || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pcr?.subjekt || vyjezd?.externiFakturace?.pcr?.subjekt || "Policie ČR"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pcr?.castkaBezDph || "-"}</div>
              </div>
            ) : null}

            {!aktivniPojistovnaVetev ? (
              <div style={inactiveFinanceCardStyle}>
                <div style={financeCardTitleStyle(false)}>
                  Výjezd – Zdravotní pojišťovna
                  {financeBadge(false, "neaktivní")}
                </div>
                <div><strong>Aktivní:</strong> NE</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.pojistovnaKm || "0"}</div>
                <div><strong>Výpočet:</strong> {prevoz?.kmRozpad?.pojistovnaKmVypocet || "-"}</div>
                <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pojistovna?.cislo || vyjezd?.externiFakturace?.pojistovna?.cislo || "-"}</div>
                <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pojistovna?.datum || vyjezd?.externiFakturace?.pojistovna?.datum || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || vyjezd?.externiFakturace?.pojistovna?.subjekt || "-"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pojistovna?.castkaBezDph || "-"}</div>
              </div>
            ) : null}

            {!aktivniRodinnaVetev ? (
              <div style={inactiveFinanceCardStyle}>
                <div style={financeCardTitleStyle(false)}>
                  Rodina – navazující zakázka
                  {financeBadge(false, "neaktivní")}
                </div>
                <div><strong>Aktivní:</strong> NE</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.rodinaKm || prevoz?.rodinnaFakturace?.km || "0"}</div>
                <div><strong>Číslo:</strong> {prevoz?.rodinnaFakturace?.cislo || "-"}</div>
                <div><strong>Datum:</strong> {prevoz?.rodinnaFakturace?.datum || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.rodinnaFakturace?.subjekt || "Rodina"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.rodinnaFakturace?.castkaBezDph || "-"}</div>
                <div><strong>Poznámka:</strong> {prevoz?.rodinnaFakturace?.poznamka || "-"}</div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

        <div
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: 14,
            border: "1px solid #dbeafe",
            background: "#ffffff",
          }}
        >
          <div
            style={{
              fontSize: 14,
              fontWeight: 900,
              color: "#1e3a8a",
              marginBottom: 10,
            }}
          >
            Rozklik fakturace výjezdu
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <details open={aktivniPcrVetev}>
              <summary style={{ cursor: "pointer", fontWeight: 800, color: "#111827" }}>
                Policie ČR {aktivniPcrVetev ? "• aktivní větev" : "• neaktivní větev"}
              </summary>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                <div><strong>Aktivní:</strong> {aktivniPcrVetev ? "ANO" : "NE"}</div>
                <div><strong>Stav:</strong> {prevoz?.externiFakturace?.pcr?.stav || vyjezd?.externiFakturace?.pcr?.stav || "-"}</div>
                <div>
                  <strong>Vizuální stav:</strong>{" "}
                  <span style={invoiceStatusBadgeStyle(pcrVisualStatus)}>{pcrVisualStatus.label}</span>
                </div>
                <div><strong>Číslo faktury:</strong> {prevoz?.externiFakturace?.pcr?.cislo || vyjezd?.externiFakturace?.pcr?.cislo || "-"}</div>
                <div><strong>Datum vystavení:</strong> {prevoz?.externiFakturace?.pcr?.datum || vyjezd?.externiFakturace?.pcr?.datum || "-"}</div>
                <div><strong>Datum splatnosti:</strong> {prevoz?.externiFakturace?.pcr?.datumSplatnosti || prevoz?.externiFakturace?.pcr?.splatnost || vyjezd?.externiFakturace?.pcr?.datumSplatnosti || vyjezd?.externiFakturace?.pcr?.splatnost || "-"}</div>
                <div><strong>Odesláno dne:</strong> {prevoz?.externiFakturace?.pcr?.odeslanoDne || vyjezd?.externiFakturace?.pcr?.odeslanoDne || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pcr?.subjekt || vyjezd?.externiFakturace?.pcr?.subjekt || "Policie ČR"}</div>
                <div><strong>Adresa:</strong> {prevoz?.externiFakturace?.pcr?.adresa || vyjezd?.externiFakturace?.pcr?.adresa || "-"}</div>
                <div><strong>E-mail:</strong> {prevoz?.externiFakturace?.pcr?.email || vyjezd?.externiFakturace?.pcr?.email || "-"}</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.pcrKm || "0"}</div>
                <div><strong>Km základ:</strong> {prevoz?.vyjezdVypocet?.pcrZakladKm || prevoz?.externiFakturace?.pcr?.kmZaklad || "-"}</div>
                <div><strong>Sazba / km:</strong> {prevoz?.vyjezdCenik?.pcrSazbaKm || "-"}</div>
                <div><strong>Km výpočet:</strong> {prevoz?.vyjezdVypocet?.pcrKmVypocet || prevoz?.externiFakturace?.pcr?.kmVypocet || "-"}</div>
                <div><strong>Paušál:</strong> {prevoz?.vyjezdCenik?.pcrPausal || "-"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pcr?.castkaBezDph || prevoz?.vyjezdVypocet?.pcrCelkemBezDph || vyjezd?.externiFakturace?.pcr?.castkaBezDph || "-"}</div>
                <div><strong>Částka s DPH:</strong> {prevoz?.externiFakturace?.pcr?.castkaSDph || vyjezd?.externiFakturace?.pcr?.castkaSDph || "-"}</div>
                <div><strong>Poslední přepočet:</strong> {prevoz?.vyjezdVypocet?.posledniPrepocet || "-"}</div>
                <div><strong>Poznámka:</strong> {prevoz?.externiFakturace?.pcr?.poznamka || vyjezd?.externiFakturace?.pcr?.poznamka || "-"}</div>
              </div>
            </details>

            <details open={aktivniPojistovnaVetev}>
              <summary style={{ cursor: "pointer", fontWeight: 800, color: "#111827" }}>
                Zdravotní pojišťovna {aktivniPojistovnaVetev ? "• aktivní větev" : "• neaktivní větev"}
              </summary>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                <div><strong>Aktivní:</strong> {aktivniPojistovnaVetev ? "ANO" : "NE"}</div>
                <div><strong>Stav:</strong> {prevoz?.externiFakturace?.pojistovna?.stav || vyjezd?.externiFakturace?.pojistovna?.stav || "-"}</div>
                <div>
                  <strong>Vizuální stav:</strong>{" "}
                  <span style={invoiceStatusBadgeStyle(pojistovnaVisualStatus)}>{pojistovnaVisualStatus.label}</span>
                </div>
                <div><strong>Číslo faktury:</strong> {prevoz?.externiFakturace?.pojistovna?.cislo || vyjezd?.externiFakturace?.pojistovna?.cislo || "-"}</div>
                <div><strong>Datum vystavení:</strong> {prevoz?.externiFakturace?.pojistovna?.datum || vyjezd?.externiFakturace?.pojistovna?.datum || "-"}</div>
                <div><strong>Datum splatnosti:</strong> {prevoz?.externiFakturace?.pojistovna?.datumSplatnosti || prevoz?.externiFakturace?.pojistovna?.splatnost || vyjezd?.externiFakturace?.pojistovna?.datumSplatnosti || vyjezd?.externiFakturace?.pojistovna?.splatnost || "-"}</div>
                <div><strong>Odesláno dne:</strong> {prevoz?.externiFakturace?.pojistovna?.odeslanoDne || vyjezd?.externiFakturace?.pojistovna?.odeslanoDne || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || vyjezd?.externiFakturace?.pojistovna?.subjekt || "-"}</div>
                <div><strong>Adresa:</strong> {prevoz?.externiFakturace?.pojistovna?.adresa || vyjezd?.externiFakturace?.pojistovna?.adresa || zaklad?.zdravotniPojistovnaAdresa || vyjezd?.zdravotniPojistovnaAdresa || "-"}</div>
                <div><strong>E-mail:</strong> {prevoz?.externiFakturace?.pojistovna?.email || vyjezd?.externiFakturace?.pojistovna?.email || zaklad?.zdravotniPojistovnaEmail || vyjezd?.zdravotniPojistovnaEmail || "-"}</div>
                <div><strong>Pojišťovna:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || vyjezd?.externiFakturace?.pojistovna?.subjekt || zaklad?.zdravotniPojistovnaNazev || vyjezd?.zdravotniPojistovnaNazev || "-"}</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.pojistovnaKm || "0"}</div>
                <div><strong>Km základ:</strong> {prevoz?.vyjezdVypocet?.pojistovnaZakladKm || prevoz?.externiFakturace?.pojistovna?.kmZaklad || "-"}</div>
                <div><strong>Sazba / km:</strong> {prevoz?.vyjezdCenik?.pojistovnaSazbaKm || "-"}</div>
                <div><strong>Koeficient:</strong> {prevoz?.externiFakturace?.pojistovna?.koeficient || prevoz?.vyjezdCenik?.pojistovnaKoeficientVychozi || prevoz?.kmRozpad?.pojistovnaKoeficient || "-"}</div>
                <div><strong>Km výpočet:</strong> {prevoz?.vyjezdVypocet?.pojistovnaKmVypocet || prevoz?.externiFakturace?.pojistovna?.kmVypocet || prevoz?.kmRozpad?.pojistovnaKmVypocet || "-"}</div>
                <div><strong>Paušál:</strong> {prevoz?.vyjezdCenik?.pojistovnaPausal || "-"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pojistovna?.castkaBezDph || prevoz?.vyjezdVypocet?.pojistovnaCelkemBezDph || vyjezd?.externiFakturace?.pojistovna?.castkaBezDph || "-"}</div>
                <div><strong>Částka s DPH:</strong> {prevoz?.externiFakturace?.pojistovna?.castkaSDph || vyjezd?.externiFakturace?.pojistovna?.castkaSDph || "-"}</div>
                <div><strong>Poslední přepočet:</strong> {prevoz?.vyjezdVypocet?.posledniPrepocet || "-"}</div>
                <div><strong>Poznámka:</strong> {prevoz?.externiFakturace?.pojistovna?.poznamka || vyjezd?.externiFakturace?.pojistovna?.poznamka || "-"}</div>
              </div>
            </details>

            <details open={aktivniRodinnaVetev}>
              <summary style={{ cursor: "pointer", fontWeight: 800, color: "#111827" }}>
                Rodina {aktivniRodinnaVetev ? "• aktivní větev" : "• neaktivní větev"}
              </summary>
              <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                <div><strong>Aktivní:</strong> {aktivniRodinnaVetev ? "ANO" : "NE"}</div>
                <div><strong>Stav:</strong> {prevoz?.rodinnaFakturace?.stav || "-"}</div>
                <div>
                  <strong>Vizuální stav:</strong>{" "}
                  <span style={invoiceStatusBadgeStyle(rodinaVisualStatus)}>{rodinaVisualStatus.label}</span>
                </div>
                <div><strong>Číslo faktury:</strong> {prevoz?.rodinnaFakturace?.cislo || "-"}</div>
                <div><strong>Datum vystavení:</strong> {prevoz?.rodinnaFakturace?.datum || "-"}</div>
                <div><strong>Datum splatnosti:</strong> {prevoz?.rodinnaFakturace?.datumSplatnosti || prevoz?.rodinnaFakturace?.splatnost || "-"}</div>
                <div><strong>Odesláno dne:</strong> {prevoz?.rodinnaFakturace?.odeslanoDne || "-"}</div>
                <div><strong>Subjekt:</strong> {prevoz?.rodinnaFakturace?.subjekt || "Rodina"}</div>
                <div><strong>Adresa:</strong> {prevoz?.rodinnaFakturace?.adresa || vypravitel?.adresa || "-"}</div>
                <div><strong>E-mail:</strong> {prevoz?.rodinnaFakturace?.email || vypravitel?.email || "-"}</div>
                <div><strong>Km:</strong> {prevoz?.kmRozpad?.rodinaKm || prevoz?.rodinnaFakturace?.km || "0"}</div>
                <div><strong>Km základ:</strong> {prevoz?.vyjezdVypocet?.rodinaZakladKm || prevoz?.rodinnaFakturace?.km || "-"}</div>
                <div><strong>Sazba / km:</strong> {prevoz?.vyjezdCenik?.rodinaSazbaKm || "-"}</div>
                <div><strong>Km výpočet:</strong> {prevoz?.vyjezdVypocet?.rodinaKmVypocet || prevoz?.rodinnaFakturace?.km || "-"}</div>
                <div><strong>Paušál:</strong> {prevoz?.vyjezdCenik?.rodinaPausal || "-"}</div>
                <div><strong>Částka bez DPH:</strong> {prevoz?.rodinnaFakturace?.castkaBezDph || prevoz?.vyjezdVypocet?.rodinaCelkemBezDph || "-"}</div>
                <div><strong>Částka s DPH:</strong> {prevoz?.rodinnaFakturace?.castkaSDph || "-"}</div>
                <div><strong>Poslední přepočet:</strong> {prevoz?.vyjezdVypocet?.posledniPrepocet || "-"}</div>
                <div><strong>Poznámka:</strong> {prevoz?.rodinnaFakturace?.poznamka || "-"}</div>
              </div>
            </details>
          </div>
        </div>

      <div
        style={{
          marginTop: 14,
          padding: 14,
          borderRadius: 14,
          background: "#ffffff",
          border: "1px dashed #d1d5db",
          color: "#374151",
          lineHeight: 1.55,
        }}
      >
                        <div
          style={{
            marginTop: 12,
            padding: 12,
            borderRadius: 14,
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            fontSize: 14,
            color: "#374151",
            lineHeight: 1.6,
          }}
        >
          <strong>Logika zakázky:</strong> výjezdová část se účtuje na <strong>{hlavniVyjezdVetevLabel}</strong>.
          <br />
          <strong>Rodinná část:</strong> {maNavazujiciRodinu
            ? "ano — navazuje obřad / kremace / pohřeb a další služby pro rodinu"
            : "ne — zatím je aktivní jen výjezdová část"}
        </div>

        <div
          style={{
            marginTop: 12,
            marginBottom: 18,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "#ffffff",
              border: "1px solid #dbeafe",
              boxShadow: "0 6px 18px rgba(37,99,235,0.06)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: "#2563eb", marginBottom: 8 }}>
              1. Výjezdová část
            </div>
            <div style={{ fontSize: 15, color: "#111827", lineHeight: 1.6 }}>
              <strong>Hlavní plátce:</strong> {hlavniVyjezdVetevLabel}
              <br />
              <strong>Režim:</strong> {zaklad?.vyjezdRezim || vyjezd?.vyjezdRezim || "-"}
            </div>
          </div>

          <div
            style={{
              padding: 14,
              borderRadius: 16,
              background: "#ffffff",
              border: "1px solid #dcfce7",
              boxShadow: "0 6px 18px rgba(34,197,94,0.06)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 800, color: "#166534", marginBottom: 8 }}>
              2. Rodinná část
            </div>
            <div style={{ fontSize: 15, color: "#111827", lineHeight: 1.6 }}>
              <strong>Navazuje:</strong> {maNavazujiciRodinu ? "ANO" : "NE"}
              <br />
              <strong>Obsah:</strong> {maNavazujiciRodinu ? "obřad, kremace/pohřeb, služby, květiny, platba" : "-"}
            </div>
          </div>
        </div>
        <div>
          <strong>Rodina:</strong> {maNavazujiciRodinu
            ? "má navazující samostatnou větev pro další služby po výjezdu."
            : "zatím nemá navazující samostatnou větev."}
        </div>
      </div>
    </div>
  );

  const obrad = order?.obrad || {};
  const sluzby = Array.isArray(order?.sluzby) ? order.sluzby : [];
  const kvetiny = order?.kvetiny || {};
  const fotky = order?.fotky || {};
  const photoCount = Number(order?.photoCount || 0) || 0;
  const hasOhledaciList = !!order?.hasOhledaciList;
  const opZesnulyCount = Number(order?.opZesnulyCount || 0) || 0;
  const opObjednavatelCount = Number(order?.opObjednavatelCount || 0) || 0;

  const kvetinyItems = useMemo(() => {
    const normalizeItem = (item) => ({
      objednano: !!item?.objednano,
      dodavatel: item?.dodavatel || "",
      typVazby: item?.typVazby || "",
      barva: item?.barva || "",
      stuhaBarva: item?.stuhaBarva || "",
      stuhaText1: item?.stuhaText1 || "",
      stuhaText2: item?.stuhaText2 || "",
      poznamka: item?.poznamka || "",
      mnozstvi: Number(item?.mnozstvi || 1) || 1,
      cenaBezDph: Number(item?.cenaBezDph || 0) || 0,
      cenaSDph: Number(item?.cenaSDph || 0) || 0,
      dph: Number(item?.dph || 0) || 0,
    });

    const isFilledItem = (item) =>
      !!(
        item?.objednano ||
        String(item?.dodavatel || "").trim() ||
        String(item?.typVazby || "").trim() ||
        String(item?.barva || "").trim() ||
        String(item?.stuhaBarva || "").trim() ||
        String(item?.stuhaText1 || "").trim() ||
        String(item?.stuhaText2 || "").trim() ||
        String(item?.poznamka || "").trim() ||
        (Number(item?.cenaSDph || 0) || 0) > 0 ||
        (Number(item?.cenaBezDph || 0) || 0) > 0
      );

    const arr = Array.isArray(kvetiny?.items) ? kvetiny.items : [];
    if (arr.length > 0) {
      return arr.map(normalizeItem).filter(isFilledItem);
    }

    const legacyItem = normalizeItem(kvetiny || {});
    return isFilledItem(legacyItem) ? [legacyItem] : [];
  }, [kvetiny]);

  const kvetinyTotals = useMemo(() => {
    return kvetinyItems.reduce(
      (acc, item) => {
        const mnozstvi = Number(item?.mnozstvi || 1) || 1;
        const bez = round2((Number(item?.cenaBezDph || 0) || 0) * mnozstvi);
        const dphPct = Number(item?.dph || 0) || 0;
        const dphCastka = round2((bez * dphPct) / 100);
        const celkem = round2(bez + dphCastka);
        acc.bez += bez;
        acc.dph += dphCastka;
        acc.celkem += celkem;
        return acc;
      },
      { bez: 0, dph: 0, celkem: 0 }
    );
  }, [kvetinyItems]);

  const kvetinyOrderHistory = useMemo(() => {
    const logs = [];
    kvetinyItems.forEach((item, itemIndex) => {
      const emailLog = Array.isArray(item?.emailLog) ? item.emailLog : [];
      emailLog.forEach((entry, logIndex) => {
        logs.push({
          itemIndex,
          logIndex,
          sentAt: entry?.sentAt || "",
          mode: entry?.mode || "",
          to: entry?.to || "",
          cc: entry?.cc || "",
          subject: entry?.subject || "",
        });
      });
    });

    logs.sort((a, b) => {
      const aa = a.sentAt ? new Date(a.sentAt).getTime() : 0;
      const bb = b.sentAt ? new Date(b.sentAt).getTime() : 0;
      return bb - aa;
    });

    return logs;
  }, [kvetinyItems]);

  const lastKvetinyOrderLog = kvetinyOrderHistory.length ? kvetinyOrderHistory[0] : null;

  const hasKvetiny = kvetinyItems.length > 0;
  const navazujiciObradSUrnou = !!order?.zaklad?.navazujiciObradSUrnou;
  const dopravaMode = String(order?.prevoz?.dopravaMode || "");
  const etapa1Km = Number(order?.prevoz?.etapy?.etapa1Km || 0) || 0;
  const etapa2Km = Number(order?.prevoz?.etapy?.etapa2Km || 0) || 0;
  const etapa3Km = Number(order?.prevoz?.etapy?.etapa3Km || 0) || 0;
  const etapyKmCelkem = etapa1Km + etapa2Km + etapa3Km;

    const sluzbyRows = sluzby
      .filter((row) => row)
      .map((row) => {
        const cenik = resolveCenikItem(row?.id);
        if (!cenik) return null;

        const rakevItem = row?.rakevId ? resolveCenikItem(row.rakevId) : null;
        const cenaAktualni =
          row?.id === "rakev" && rakevItem
            ? Number(rakevItem.cenaBezDph) || 0
            : row?.cenaBezDphOverride !== "" && row?.cenaBezDphOverride !== undefined
              ? Number(row.cenaBezDphOverride) || 0
              : Number(cenik.cenaBezDph) || 0;

        const qty = Number(row?.mnozstvi) || 0;
        const sums = calcServiceRow(cenaAktualni, cenik.dph, qty);
        const suspiciousPrice = isSuspiciousPrice(cenaAktualni);
        const sekceKey = String(cenik?.sekceKey || "ostatni");
        const subgroupKey = String(cenik?.subgroupKey || "");

        return {
          id: row?.id || "",
          nazev:
            row?.id === "rakev" && rakevItem
              ? `${cenik.nazev} – ${rakevItem.nazev}`
              : cenik.nazev,
          jednotka: cenik.jednotka || "ks",
          mnozstvi: qty,
          cenaBezDph: cenaAktualni,
          dphPct: Number(cenik.dph) || 0,
          bez: sums.bez,
          dph: sums.dph,
          celkem: sums.celkem,
          suspiciousPrice,
          sekceKey,
          subgroupKey,
          sectionTitle: getFinalSectionLabel(sekceKey),
          subgroupTitle: getFinalSubgroupLabel(sekceKey, subgroupKey),
        };
      })
      .filter(Boolean);

  const sluzbyTotals = sluzbyRows.reduce(
    (acc, row) => {
      acc.bez += row.bez;
      acc.dph += row.dph;
      acc.celkem += row.celkem;
      return acc;
    },
    { bez: 0, dph: 0, celkem: 0 }
  );

    const groupedServiceRows = Object.values(
      sluzbyRows.reduce((acc, row) => {
        const sectionKey = String(row?.sekceKey || "ostatni");
        const subgroupKey = String(row?.subgroupKey || "");
        const groupKey = `${sectionKey}::${subgroupKey}`;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            key: groupKey,
            sectionKey,
            subgroupKey,
            title: getFinalSectionLabel(sectionKey),
            subgroupTitle: getFinalSubgroupLabel(sectionKey, subgroupKey),
            rows: [],
            totals: { bez: 0, dph: 0, celkem: 0 },
          };
        }

        acc[groupKey].rows.push(row);
        acc[groupKey].totals.bez += row.bez;
        acc[groupKey].totals.dph += row.dph;
        acc[groupKey].totals.celkem += row.celkem;
        return acc;
      }, {})
    ).sort((a, b) => {
      const sa = String(a.title || "");
      const sb = String(b.title || "");
      if (sa !== sb) return sa.localeCompare(sb, "cs");
      return String(a.subgroupTitle || "").localeCompare(String(b.subgroupTitle || ""), "cs");
    });

    const sluzbyCount = sluzbyRows.length;

  const rawZaloha =
    order?.finance?.zalohaCastka ??
    order?.platba?.zalohovaPlatba ??
    order?.platba?.zalohaCastka ??
    order?.zalohaCastka ??
    0;

  const zalohaCastka = round2(Number(rawZaloha) || 0);

  const rawUhrazeno =
    order?.finance?.uhrazenoCelkem ??
    order?.platba?.uhrazenoCelkem ??
    order?.uhrazenoCelkem;

  const uhrazenoCelkem = round2(
    rawUhrazeno === "" || rawUhrazeno === null || rawUhrazeno === undefined
      ? zalohaCastka
      : (Number(rawUhrazeno) || 0)
  );

  const zpusobUhrady =
    order?.platba?.zpusobUhrady ||
    order?.finance?.zpusobUhrady ||
    "-";

  const datumUhrady =
    order?.platba?.datumUhrady ||
    order?.finance?.datumUhrady ||
    "-";

  const poznamkaPlatba =
    order?.platba?.poznamkaPlatba ||
    order?.finance?.poznamkaPlatba ||
    "-";

  const finalTotals = {
    bez: round2(sluzbyTotals.bez + (hasKvetiny ? kvetinyTotals.bez : 0)),
    dph: round2(sluzbyTotals.dph + (hasKvetiny ? kvetinyTotals.dph : 0)),
    celkem: round2(sluzbyTotals.celkem + (hasKvetiny ? kvetinyTotals.celkem : 0)),
  };

  const doplatekCastka = round2(Math.max(0, finalTotals.celkem - uhrazenoCelkem));
  const preplatekCastka = round2(Math.max(0, uhrazenoCelkem - finalTotals.celkem));

  const financniStav =
    preplatekCastka > 0 ? "Přeplaceno" :
    doplatekCastka === 0 && finalTotals.celkem > 0 ? "Uhrazeno" :
    uhrazenoCelkem > 0 ? "Částečně uhrazeno" :
    "Neuhrazeno";
  const suspiciousServiceRows = sluzbyRows.filter((row) => row?.suspiciousPrice);
  const suspiciousServiceCount = suspiciousServiceRows.length;

  const typZakazky = zaklad.typZakazky || "";
  const isKremace = typZakazky === "kremace" || typZakazky === "kremace_bez_obradu";
  const isPohreb = typZakazky === "pohreb";
  const isPrevoz = typZakazky === "prevoz";

  const zesnulyDisplay =
    zaklad.zesnuly ||
    [vstupni.jmenoZesnuleho, vstupni.prijmeniZesnuleho].filter(Boolean).join(" ") ||
    [zesnula.jmeno, zesnula.prijmeni].filter(Boolean).join(" ") ||
    "-";

  const vypravitelDisplay =
    zaklad.vypravitel ||
    [vypravitel.jmeno, vypravitel.prijmeni].filter(Boolean).join(" ") ||
    "-";

  const selectedTemplateName = String(order?.meta?.selectedTemplateName || "").trim();

  const objednatelDisplay =
    [vypravitel.jmeno, vypravitel.prijmeni].filter(Boolean).join(" ") ||
    zaklad.vypravitel ||
    "-";

  const zesnulyPrintDisplay =
    [zesnula.jmeno, zesnula.prijmeni].filter(Boolean).join(" ") ||
    [vstupni.jmenoZesnuleho, vstupni.prijmeniZesnuleho].filter(Boolean).join(" ") ||
    zaklad.zesnuly ||
    "-";

  const datumNarozeniPrint = zesnula.datumNarozeni || vstupni.datumNarozeni || "-";
  const datumUmrtiPrint = zesnula.datumUmrti || vstupni.datumUmrti || "-";
  const bydlistePrint = zesnula.trvaleBydliste || zesnula.adresaUmrti || vstupni.adresaUmrti || "-";
  const telefonObjednatelPrint = vypravitel.telefon || "-";
  const emailObjednatelPrint = vypravitel.email || "-";
  const mistoUmrtiPrint = zesnula.mistoUmrti || vstupni.mistoUmrti || prevoz.mistoOdvozu || "-";
  const datumSjednaniPrint = zaklad.datumSjednani || "-";
  const zalohaPrint = formatMoney(zalohaCastka);
  const uhrazenoPrint = formatMoney(uhrazenoCelkem);
  const doplatekPrint = formatMoney(doplatekCastka);
  const zpusobUhradyPrint = zpusobUhrady || "-";
  const datumUhradyPrint = datumUhrady || "-";

  const printGroups = groupedServiceRows.map((group) => ({
    ...group,
    printTitle:
      group.subgroupTitle && group.subgroupTitle !== "Bez podboxu"
        ? `${group.title} – ${group.subgroupTitle}`
        : group.title,
  }));

  const printPart1 = printGroups.filter((group, index) => index % 2 === 0);
  const printPart2 = printGroups.filter((group, index) => index % 2 === 1);

  const summaryType =
    isKremace ? "Kremace" :
    isPohreb ? "Pohřeb" :
    isPrevoz ? "Pouze převoz" :
    (typZakazky || "-");

  const dopravaSummary = getDopravaSummary(dopravaMode, etapyKmCelkem);

  const maRodinnouCast = maNavazujiciRodinu;

  const rodinnaCastCard = (
    <div
      style={{
        marginTop: 16,
        marginBottom: 18,
        padding: 16,
        borderRadius: 18,
        border: "1px solid #e5e7eb",
        background: maRodinnouCast ? "#f0fdf4" : "#f9fafb",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 900, color: "#111827", marginBottom: 8 }}>
        Rodinná část zakázky
      </div>

      <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
        {maRodinnouCast ? (
          <>
            Na výjezd navazuje běžná rodinná část zakázky.
            <br />
            Patří sem <strong>obřad / kremace / pohřeb</strong>, <strong>květiny</strong> a <strong>výběr služeb</strong>.
          </>
        ) : (
          <>
            Zatím existuje jen výjezdová část.
            <br />
            Rodinná část ještě nebyla založena nebo na výjezd nenavazuje.
          </>
        )}
      </div>
    </div>
  );

  const sectionStates = [
    { key: "zaklad", label: "Základ", ok: hasAnyFilled(zaklad) },
    { key: "vstupni", label: "Vstupní informace", ok: hasAnyFilled(vstupni) },
    { key: "vypravitel", label: "Vypravitel", ok: hasAnyFilled(vypravitel) },
    { key: "zesnula", label: "Zesnulá osoba", ok: hasAnyFilled(zesnula) },
    { key: "prevoz", label: "Převoz", ok: hasAnyFilled(prevoz) },
    { key: "rodinna", label: "Rodinná část", ok: maRodinnouCast },
    ...(maRodinnouCast ? [{ key: "obrad", label: "Obřad / kremace / pohřeb", ok: hasAnyFilled(obrad) }] : []),
    ...(maRodinnouCast ? [{ key: "kvetiny", label: "Květiny", ok: hasKvetiny }] : []),
    ...(maRodinnouCast ? [{ key: "sluzby", label: "Výběr služeb", ok: sluzbyRows.length > 0 }] : []),
  ];

  const handleGenerateDocuments = async () => {
    const orderId = String(currentOrderId || "").trim();

    if (!orderId) {
      setGenerateMsg("Nejdřív ulož zakázku, aby měla své ID.");
      return;
    }

    if (!isKremace) {
      setGenerateMsg("Teď jsou napojené jen kremační dokumenty. Pohřební dopojíme v dalším kroku.");
      return;
    }

    const docs = [
      "Rakvenka.pdf",
      "Plna_moc_souhlas.pdf",
      "Protokol_o_predani_zesnuleho_CB.pdf",
      "Plna_moc_vyzvednuti_urny_CB.pdf",
    ];

    try {
      setGenerating(true);
      setGenerateMsg("Generuji dokumenty...");

      const headers = {
        Accept: "application/zip,application/octet-stream,application/json",
        "Content-Type": "application/json",
      };

      const token = readToken();
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(apiUrl("/api/generuj-dokumenty"), {
        method: "POST",
        headers,
        body: JSON.stringify({
          id: orderId,
          orderId,
          slozkaSablon: "kremace",
          vybraneDokumenty: docs,
          formData: {
            ...(order || {}),
            id: orderId,
            orderId,
            zakazkaId: orderId,
          },
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        let data = null;
        try {
          data = text ? JSON.parse(text) : null;
        } catch {
          data = text;
        }
        throw new Error(
          (data && typeof data === "object" && (data.error || data.message)) ||
          String(data || `HTTP ${res.status}`)
        );
      }

      const blob = await res.blob();
      const filename = getFilenameFromDisposition(
        res.headers.get("content-disposition"),
        `${orderId}_kremace_dokumenty.zip`
      );

      downloadBlob(blob, filename);
      setGenerateMsg("Dokumenty byly vygenerované a stažené.");
    } catch (e) {
      setGenerateMsg(`Generování selhalo: ${String(e?.message || e)}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={cardStyle}>
      {vyjezdRozpadBox}

      <div style={{
        marginBottom: 18,
        padding: 16,
        borderRadius: 18,
        border: "1px solid #e5e7eb",
        background: "#f9fafb",
      }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 10 }}>
          Externí fakturace výjezdu
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
          <div style={{ padding: 14, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Faktura PČR</div>
            <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pcr?.cislo || vyjezd?.externiFakturace?.pcr?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pcr?.datum || vyjezd?.externiFakturace?.pcr?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pcr?.subjekt || vyjezd?.externiFakturace?.pcr?.subjekt || "-"}</div>
            <div><strong>Km základ:</strong> {prevoz?.externiFakturace?.pcr?.kmZaklad || "0"}</div>
            <div><strong>Km výpočet:</strong> {prevoz?.externiFakturace?.pcr?.kmVypocet || "0"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pcr?.castkaBezDph || "-"}</div>
            <div><strong>Služby:</strong> výjezd, převoz na pitvu, návrat na firmu</div>
          </div>

          <div style={{ padding: 14, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Faktura pojišťovna</div>
            <div><strong>Číslo:</strong> {prevoz?.externiFakturace?.pojistovna?.cislo || vyjezd?.externiFakturace?.pojistovna?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.externiFakturace?.pojistovna?.datum || vyjezd?.externiFakturace?.pojistovna?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.externiFakturace?.pojistovna?.subjekt || vyjezd?.externiFakturace?.pojistovna?.subjekt || "-"}</div>
            <div><strong>Km základ:</strong> {prevoz?.externiFakturace?.pojistovna?.kmZaklad || "0"}</div>
            <div><strong>Koeficient:</strong> {prevoz?.externiFakturace?.pojistovna?.koeficient || "-"}</div>
            <div><strong>Km výpočet:</strong> {prevoz?.externiFakturace?.pojistovna?.kmVypocet || "-"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.externiFakturace?.pojistovna?.castkaBezDph || "-"}</div>
            <div><strong>Služby:</strong> naložený převoz na pitvu, naložený převoz z pitvy</div>
          </div>

          <div style={{ padding: 14, borderRadius: 14, background: "#fff", border: "1px solid #e5e7eb" }}>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Rodinná fakturace</div>
            <div><strong>Číslo:</strong> {prevoz?.rodinnaFakturace?.cislo || "-"}</div>
            <div><strong>Datum:</strong> {prevoz?.rodinnaFakturace?.datum || "-"}</div>
            <div><strong>Subjekt:</strong> {prevoz?.rodinnaFakturace?.subjekt || "Rodina"}</div>
            <div><strong>Km:</strong> {prevoz?.rodinnaFakturace?.km || "0"}</div>
            <div><strong>Částka bez DPH:</strong> {prevoz?.rodinnaFakturace?.castkaBezDph || "-"}</div>
            <div><strong>Poznámka:</strong> {prevoz?.rodinnaFakturace?.poznamka || "-"}</div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: "clamp(26px, 5vw, 34px)", fontWeight: 900, color: "#111827", lineHeight: 1.05 }}>
            Finální objednávka
          </div>
          <div style={{ marginTop: 8, color: "#6b7280", fontSize: 16 }}>
            Přehled aktuálně vyplněných údajů v nové větvi Pohřebka 2.
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div style={badgeStyle}>ID zakázky: {currentOrderId || "nová"}</div>
            <div style={topActionsWrapStyle}>
              <button type="button" onClick={() => saveOrder()} style={{ ...buttonPrimary, ...topActionButtonStyle, flex: "1 1 180px" }}>
              {saving ? "Ukládám..." : "Uložit zakázku"}
            </button>

            <button
              type="button"
              onClick={handleGenerateDocuments}
              disabled={generating || saving}
              style={{
                ...buttonSecondary,
                  ...topActionButtonStyle,
                  flex: "1 1 180px",
                opacity: generating || saving ? 0.7 : 1,
                cursor: generating || saving ? "not-allowed" : "pointer",
              }}
            >
              {generating ? "Generuji..." : "Generovat dokumenty"}
            </button>

              <button
                type="button"
                onClick={handlePrintFinal}
                style={{ ...buttonSecondary, ...topActionButtonStyle, flex: "1 1 180px" }}
              >
                Vytisknout
              </button>

              <button
                type="button"
                onClick={handleExportPdf}
                style={{ ...buttonSecondary, ...topActionButtonStyle, flex: "1 1 180px" }}
              >
                Export PDF
              </button>
          </div>
        </div>
      </div>

      <div style={heroStyle}>
        <div style={{ fontSize: 13, opacity: 0.82, fontWeight: 700 }}>Shrnutí zakázky</div>
        
        <div
          style={{
            marginBottom: 18,
            borderRadius: 18,
            padding: 16,
            ...recCardMeta,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 260, flex: "1 1 320px" }}>
              <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                Řeč / řečnice
              </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 800,
                      background: "#ffffff",
                      color: recCardMeta.color,
                      border: `1px solid ${recCardMeta.color}33`,
                    }}
                  >
                    {recStatus}
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 800,
                      background: familyWorkflowMeta.background,
                      color: familyWorkflowMeta.color,
                      border: `1px solid ${familyWorkflowMeta.border}`,
                    }}
                  >
                    Rodina: {familyWorkflowMeta.label}
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 700,
                      border: "1px solid rgba(17,24,39,0.12)",
                      background: "#ffffff",
                      color: "#111827",
                    }}
                  >
                    Potvrzení: {familyConfirmed ? "ano" : "ne"}
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 700,
                      border: "1px solid rgba(17,24,39,0.12)",
                      background: "#ffffff",
                      color: "#111827",
                    }}
                  >
                    Uzamčení: {familyLocked ? "ano" : "ne"}
                  </span>

                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      borderRadius: 999,
                      padding: "8px 12px",
                      fontWeight: 700,
                      border: "1px solid rgba(17,24,39,0.12)",
                      background: "#ffffff",
                      color: "#111827",
                    }}
                  >
                    Podklady: {[
                      recHasFamily ? "rodina" : "",
                      recHasLife ? "život" : "",
                      recHasCharacter ? "charakter" : "",
                      recHasSensitive ? "citlivé body" : "",
                      recHasForm ? "forma" : "",
                    recHasHudba ? "hudba" : "",
                      ].filter(Boolean).join(", ") || "žádné"}
                  </span>
                </div>

              <div style={{ fontSize: 14, fontWeight: 700, color: "#374151", lineHeight: 1.5 }}>
                {recNotes
                  ? recNotes.slice(0, 140) + (recNotes.length > 140 ? "..." : "")
                  : recDraft
                    ? "Jsou vložené podklady nebo návrh řeči."
                    : "Zatím nejsou vložené podklady k řeči."}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setRecExpanded((v) => !v)}
              style={{
                ...buttonSecondary,
                background: "#ffffff",
                minWidth: 150,
              }}
            >
              {recExpanded ? "Skrýt detail" : "Zobrazit detail"}
            </button>
          </div>

          {recExpanded ? (
            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6, textTransform: "uppercase" }}>
                  Poznámky pro řečnici
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.5 }}>
                  {recNotes || "Zatím prázdné"}
                </div>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6, textTransform: "uppercase" }}>
                  Návrh řeči
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {recDraft || "Zatím nevytvořen"}
                </div>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6, textTransform: "uppercase" }}>
                  Připravenost podkladů
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Rodina: {recHasFamily ? "ano" : "ne"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Život: {recHasLife ? "ano" : "ne"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Charakter: {recHasCharacter ? "ano" : "ne"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Citlivé body: {recHasSensitive ? "ano" : "ne"}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Forma řeči: {recHasForm ? "ano" : "ne"}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Hudba: {recHasHudba ? "ano" : "ne"}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Sdílení rodině: {familyWorkflowMeta.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Potvrzeno rodinou: {familyConfirmed ? "ano" : "ne"}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Uzamčeno: {familyLocked ? "ano" : "ne"}</div>
                </div>

                {recHasHudba ? (
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6, textTransform: "uppercase" }}>
                      Hudba k obřadu
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      {rec?.hudba?.nastup ? (
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#f9fafb" }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
                            Nástup
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{rec.hudba.nastup}</div>
                        </div>
                      ) : null}

                      {rec?.hudba?.predProslovem ? (
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#f9fafb" }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
                            Před proslovem
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{rec.hudba.predProslovem}</div>
                        </div>
                      ) : null}

                      {rec?.hudba?.zaver ? (
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#f9fafb" }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", marginBottom: 4 }}>
                            Po proslovu / závěr
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>{rec.hudba.zaver}</div>
                        </div>
                      ) : null}

                      {recHudbaDalsiList.length ? (
                        <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#f9fafb" }}>
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", marginBottom: 6 }}>
                            Další hudba
                          </div>
                          <div style={{ display: "grid", gap: 6 }}>
                            {recHudbaDalsiList.map((x, i) => (
                              <div key={`hudba-dalsi-${i}`} style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                                {i + 1}. {x}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>

      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 6 }}>{zesnulyDisplay}</div>
        <div style={{ marginTop: 8, opacity: 0.95 }}>
          {summaryType} • {zaklad.datumSjednani || "-"}
        </div>
        <div style={{ marginTop: 8, opacity: 0.88 }}>
          Vypravitel: {vypravitelDisplay}
        </div>
        {selectedTemplateName ? (
          <div style={{ marginTop: 8, opacity: 0.88 }}>
            Vzor: <strong>{selectedTemplateName}</strong>
          </div>
        ) : null}

        <div
        style={{
          marginTop: 18,
          border: "1px solid #334155",
          borderRadius: 22,
          padding: 20,
          background: "#0f172a",
          color: "#f8fafc",
          boxShadow: "0 10px 30px rgba(2,6,23,0.28)",
        }}
      >
        <div
          style={{
            fontSize: 22,
            fontWeight: 900,
            marginBottom: 16,
            color: "#ffffff",
            letterSpacing: "0.01em",
          }}
        >
          Zajištění obřadu
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 12,
          }}
        >
          <div>
            <strong>Zesnulý</strong>
            <div>{renderSummaryValue(zesnulyJmeno)}</div>
          </div>

          <div>
            <strong>Objednavatel</strong>
            <div>{renderSummaryValue(objednavatelJmeno)}</div>
          </div>

          <div>
            <strong>Typ zakázky</strong>
            <div>{renderSummaryValue(zakladSummary?.typZakazky)}</div>
          </div>

          <div>
            <strong>Datum obřadu</strong>
            <div>{renderSummaryValue(obradSummary?.datum)}</div>
          </div>

          <div>
            <strong>Čas obřadu</strong>
            <div>{renderSummaryValue(obradSummary?.cas)}</div>
          </div>

          <div>
            <strong>Místo obřadu</strong>
            <div>{renderSummaryValue(obradSummary?.misto)}</div>
          </div>

          <div>
            <strong>Adresa místa</strong>
            <div>{renderSummaryValue(obradSummary?.mistoAdresa)}</div>
          </div>

          <div>
            <strong>ID místa</strong>
            <div>{renderSummaryValue(obradSummary?.mistoId)}</div>
          </div>

          <div>
            <strong>Skupina místa</strong>
            <div>{renderSummaryValue(obradSummary?.mistoSkupina)}</div>
          </div>

          <div>
            <strong>Varianta rozloučení</strong>
            <div>{renderSummaryValue(obradSummary?.ceremonyVariant)}</div>
          </div>

          <div>
            <strong>Typ kostelního rozloučení</strong>
            <div>{renderSummaryValue(obradSummary?.churchServiceType)}</div>
          </div>

          <div>
            <strong>Vedoucí obřadu</strong>
            <div>{renderStaffSummary(vedouciObradu)}</div>
          </div>

          <div>
            <strong>Obřadní služba / druhý člověk</strong>
            <div>{renderStaffSummary(obradniSluzba)}</div>
          </div>

          <div>
            <strong>Řečník</strong>
            <div>{renderSpeakerSummary(obradSummary?.recnikTyp || recnikOsoba)}</div>
          </div>

          <div>
            <strong>Hudba</strong>
            <div>{renderMusicTypeSummary(obradSummary?.hudbaTyp)}</div>
          </div>

          <div>
            <strong>Hudbu zajišťuje</strong>
            <div>{renderMusicPersonSummary(obradSummary?.hudbaZajistuje || hudbaOsoba)}</div>
          </div>

          <div>
            <strong>Farář</strong>
            <div>{renderSummaryValue(obradSummary?.fararJmeno)}</div>
          </div>

          <div>
            <strong>Auto pro obřad</strong>
            <div>{renderVehicleSummary(hlavniVuz)}</div>
          </div>

          <div>
            <strong>Řidič převozu</strong>
            <div>{renderStaffSummary(ridicPrevozu)}</div>
          </div>

          <div>
            <strong>Doprovod převozu</strong>
            <div>{renderStaffSummary(doprovodPrevozu)}</div>
          </div>

          <div>
            <strong>Převozové auto</strong>
            <div>{renderVehicleSummary(prevozoveAuto)}</div>
          </div>
        </div>

        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <strong style={{ color: "#ffffff" }}>Poznámka</strong>
          <div style={{ marginTop: 6, color: "#e2e8f0", lineHeight: 1.5 }}>
            {renderSummaryValue(obradSummary?.poznamka || prevozSummary?.poznamka || zakladSummary?.poznamka)}
          </div>
        </div>
      </div>

      <div style={badgeRowStyle}>
          <span style={softBadgeStyle}>ID: {currentOrderId || "nová"}</span>
          <span style={softBadgeStyle}>Typ: {summaryType}</span>
          <span style={softBadgeStyle}>Datum sjednání: {zaklad.datumSjednani || "-"}</span>
          {dopravaMode ? <span style={softBadgeStyle}>Doprava: {getDopravaModeLabel(dopravaMode)}</span> : null}
        </div>

        <div style={{ ...badgeRowStyle, marginTop: 10 }}>
          <span style={softBadgeStyle}>Vybráno služeb: {sluzbyCount}</span>
          <span style={softBadgeStyle}>Bez DPH: {formatMoney(finalTotals.bez)}</span>
          <span style={softBadgeStyle}>DPH: {formatMoney(finalTotals.dph)}</span>
          <span style={softBadgeStyle}>Celkem: {formatMoney(finalTotals.celkem)}</span>
          {suspiciousServiceCount ? (
            <span
              style={{
                ...softBadgeStyle,
                background: "#fef2f2",
                color: "#991b1b",
                border: "1px solid #fecaca",
              }}
            >
              Podezřelé ceny: {suspiciousServiceCount}
            </span>
          ) : null}
        </div>
      </div>

      <div style={infoBoxStyle}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#6b7280", marginBottom: 6 }}>
          Generování dokumentů
        </div>
        <div style={{ fontSize: 15, color: "#111827", fontWeight: 700 }}>
          {isKremace
            ? "Aktuálně jsou napojené kremační dokumenty: Rakvenka, Plná moc – souhlas, Předávací protokol CB, Plná moc k vyzvednutí urny CB."
            : isPohreb
              ? "Tlačítko je připravené, ale pohřební dokumenty ještě nejsou napojené."
              : isPrevoz
                ? "Samostatný převoz zatím nemá vlastní generované dokumenty."
                : "Dokumenty budou záviset na zvoleném typu zakázky."}
        </div>
        {generateMsg ? (
          <div style={{ marginTop: 10, fontSize: 14, fontWeight: 800, color: "#374151" }}>
            {generateMsg}
          </div>
        ) : null}
      </div>

        <div style={sectionStyle}>
          <div style={titleStyle}>Finanční přehled</div>
          <div style={{ color: "#6b7280", fontSize: 14, marginBottom: 14 }}>
            Průběžný souhrn plateb a doplatku k této zakázce.
          </div>

          <div style={financeGridStyle}>
            <div style={financeCardStyle}>
              <div style={financeLabelStyle}>Cena celkem</div>
              <div style={financeValueStyle}>{formatMoney(finalTotals.celkem)}</div>
            </div>

            <div style={financeCardStyle}>
              <div style={financeLabelStyle}>Záloha</div>
              <div style={financeValueStyle}>{formatMoney(zalohaCastka)}</div>
            </div>

            <div style={financeCardStyle}>
              <div style={financeLabelStyle}>Uhrazeno celkem</div>
              <div style={financeValueStyle}>{formatMoney(uhrazenoCelkem)}</div>
            </div>

            <div style={financeCardStyle}>
              <div style={financeLabelStyle}>Doplatek</div>
              <div style={financeValueStyle}>{formatMoney(doplatekCastka)}</div>
            </div>

            <div style={financeCardStyle}>
              <div style={financeLabelStyle}>Přeplatek</div>
              <div style={financeValueStyle}>{formatMoney(preplatekCastka)}</div>
            </div>

            <div style={financeStateStyle(financniStav)}>
              <div style={financeLabelStyle}>Finanční stav</div>
              <div style={{ marginTop: 6, fontSize: 20, fontWeight: 900 }}>
                {financniStav}
              </div>
            </div>
          </div>

            <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
              <div><strong>Způsob úhrady:</strong> {zpusobUhrady}</div>
              <div><strong>Datum úhrady:</strong> {datumUhrady}</div>
              <div><strong>Poznámka k platbě:</strong> {poznamkaPlatba}</div>
              <div><strong>Záloha zaplacena:</strong> {formatMoney(zalohaCastka)}</div>
              <div><strong>Uhrazeno celkem:</strong> {formatMoney(uhrazenoCelkem)}</div>
              <div><strong>Zbývá doplatit:</strong> {formatMoney(doplatekCastka)}</div>
            </div>
        </div>

        <div ref={printAreaRef} style={printSheetStyle}>
            <div style={printBrandWrapStyle}>
              <img
                src={`/app/branding/logo.png?v=${import.meta.env.VITE_BUILD_TS || "1"}`}
                alt="Logo pohřební služby"
                style={printBrandImageStyle}
              />
            </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#111827" }}>
            
          </div>
          <div style={{ marginTop: 4, fontSize: 14, color: "#4b5563" }}>
            
          </div>

          <div style={printTopGridStyle}>
            <div style={printCellStyle}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Objednatel</div>
              <div><strong>{objednatelDisplay}</strong></div>
              <div>Telefon: {telefonObjednatelPrint}</div>
              <div>E-mail: {emailObjednatelPrint}</div>
              <div>Poznámka: {vypravitel.poznamka || "-"}</div>
            </div>

            <div style={{ ...printCellStyle, borderRight: "none" }}>
              <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Zesnulý / zesnulá</div>
              <div><strong>{zesnulyPrintDisplay}</strong></div>
              <div>Narození: {datumNarozeniPrint}</div>
              <div>Úmrtí: {datumUmrtiPrint}</div>
              <div>Bydliště: {bydlistePrint}</div>
              <div>Místo úmrtí: {mistoUmrtiPrint}</div>
            </div>
          </div>

          <div style={{ border: "1px solid #111827", borderTop: "none", padding: 10 }}>
            <strong>Objednávka č.:</strong> {currentOrderId || "Nová zakázka"} &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Typ:</strong> {summaryType} &nbsp;&nbsp;|&nbsp;&nbsp;
            <strong>Datum sjednání:</strong> {datumSjednaniPrint}
            {selectedTemplateName ? (
              <>
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>Vzor:</strong> {selectedTemplateName}
              </>
            ) : null}
          </div>

          <div style={printTableWrapStyle}>
            <div>
              <table style={printTableStyle}>
                <thead>
                  <tr>
                    <th style={printThStyle}>ČÁST 1 – Název</th>
                    <th style={printThStyle}>bez DPH</th>
                    <th style={printThStyle}>s DPH</th>
                  </tr>
                </thead>
                <tbody>
                    {printPart1.length ? printPart1.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {group.printTitle}
                          </td>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {formatMoney(group.totals.bez)}
                          </td>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {formatMoney(group.totals.celkem)}
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <tr
                            key={`p1-${group.key}-${row.id}`}
                            style={row.suspiciousPrice ? { background: "#fef2f2" } : undefined}
                          >
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {row.nazev}
                            </td>
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {formatMoney(row.bez)}
                            </td>
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {formatMoney(row.celkem)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    )) : (
                      <tr>
                        <td style={printTdStyle}>—</td>
                        <td style={printTdStyle}>—</td>
                        <td style={printTdStyle}>—</td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            <div>
              <table style={printTableStyle}>
                <thead>
                  <tr>
                    <th style={printThStyle}>ČÁST 2 – Název</th>
                    <th style={printThStyle}>bez DPH</th>
                    <th style={printThStyle}>s DPH</th>
                  </tr>
                </thead>
                <tbody>
                    {printPart2.length ? printPart2.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {group.printTitle}
                          </td>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {formatMoney(group.totals.bez)}
                          </td>
                          <td style={{ ...printTdStyle, fontWeight: 800, background: "#f9fafb" }}>
                            {formatMoney(group.totals.celkem)}
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <tr
                            key={`p2-${group.key}-${row.id}`}
                            style={row.suspiciousPrice ? { background: "#fef2f2" } : undefined}
                          >
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {row.nazev}
                            </td>
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {formatMoney(row.bez)}
                            </td>
                            <td
                              style={{
                                ...printTdStyle,
                                color: row.suspiciousPrice ? "#991b1b" : printTdStyle.color,
                                fontWeight: row.suspiciousPrice ? 800 : printTdStyle.fontWeight,
                              }}
                            >
                              {formatMoney(row.celkem)}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    )) : (
                      <tr>
                        <td style={printTdStyle}>—</td>
                        <td style={printTdStyle}>—</td>
                        <td style={printTdStyle}>—</td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={printTotalsStyle}>
            <div style={{ fontSize: 13, color: "#374151" }}>
              <div><strong>Převoz:</strong> {getDopravaSummary(dopravaMode, etapyKmCelkem) || "-"}</div>
              <div style={{ marginTop: 4 }}><strong>Obřad:</strong> {obrad.misto || obrad.krematorium || obrad.hrbitov || "-"}</div>
              <div style={{ marginTop: 4 }}><strong>Řečník:</strong> {renderSpeakerSummary(obradSummary?.recnikTyp || recnikOsoba)}</div>
              <div style={{ marginTop: 10 }}><strong>Způsob úhrady:</strong> {zpusobUhradyPrint}</div>
              <div style={{ marginTop: 4 }}><strong>Datum úhrady:</strong> {datumUhradyPrint}</div>
              <div style={{ marginTop: 4 }}><strong>Záloha:</strong> {zalohaPrint}</div>
              <div style={{ marginTop: 4 }}><strong>Uhrazeno celkem:</strong> {uhrazenoPrint}</div>
              <div style={{ marginTop: 4 }}><strong>Doplatek:</strong> {doplatekPrint}</div>
            </div>

            <table style={printTableStyle}>
              <tbody>
                <tr>
                  <td style={printTdStyle}><strong>Základ DPH</strong></td>
                  <td style={printTdStyle}>{formatMoney(finalTotals.bez)}</td>
                </tr>
                <tr>
                  <td style={printTdStyle}><strong>DPH</strong></td>
                  <td style={printTdStyle}>{formatMoney(finalTotals.dph)}</td>
                </tr>
                <tr>
                  <td style={printTdStyle}><strong>Cena celkem</strong></td>
                  <td style={printTdStyle}><strong>{formatMoney(finalTotals.celkem)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {suspiciousServiceCount ? (
          <div
            style={{
              marginBottom: 18,
              padding: 12,
              borderRadius: 12,
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 14,
              fontWeight: 800,
            }}
          >
            Pozor: zakázka obsahuje {suspiciousServiceCount} služeb s nulovou nebo neplatnou cenou.
            Zkontroluj Výběr služeb a aktivní ceník.
          </div>
        ) : null}

      {rodinnaCastCard}

      <div style={statusGridStyle}>
        {sectionStates.map((item) => (
          <div key={item.label} style={statusCardStyle(item.ok)}>
            <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.8 }}>Sekce</div>
            <div style={{ marginTop: 4, fontSize: 16, fontWeight: 900 }}>{item.label}</div>
            <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700 }}>
              {item.ok ? "Obsahuje data" : "Zatím prázdné"}
            </div>
          </div>
        ))}
      </div>

      <div style={gridStyle}>
        <Section title="Základ">
          <Row label="Typ zakázky" value={summaryType} />
          <Row label="ID zakázky" value={currentOrderId || "Nová zakázka"} />
          <Row label="Rodinná část" value={maRodinnouCast ? "Ano" : "Ne"} />
          {selectedTemplateName ? <Row label="Aktivní vzor" value={selectedTemplateName} /> : null}
          {String(zaklad?.typZakazky || "") === "kremace_bez_obradu" ? (
            <Row label="Následné rozloučení s urnou" value={navazujiciObradSUrnou ? "Ano" : "Ne"} />
          ) : null}
          {dopravaMode ? <Row label="Režim dopravy" value={getDopravaModeLabel(dopravaMode)} /> : null}
          {dopravaSummary ? <FilledRow label="Shrnutí dopravy" value={dopravaSummary} /> : null}
          <FilledRow label="Datum sjednání" value={zaklad.datumSjednani} />
          <FilledRow label="Zesnulý" value={zaklad.zesnuly} />
          <FilledRow label="Vypravitel" value={zaklad.vypravitel} />
          <FilledRow label="Poznámka" value={zaklad.poznamka} />
        </Section>

        <Section title="Vstupní informace">
          <FilledRow label="Jméno" value={vstupni.jmenoZesnuleho} />
          <FilledRow label="Příjmení" value={vstupni.prijmeniZesnuleho} />
          <FilledRow label="Rodné číslo" value={vstupni.rodneCislo} />
          <FilledRow label="Datum narození" value={vstupni.datumNarozeni} />
          <FilledRow label="Datum úmrtí" value={vstupni.datumUmrti} />
          <FilledRow label="Čas úmrtí" value={vstupni.casUmrti} />
          <FilledRow label="Místo úmrtí" value={vstupni.mistoUmrti} />
          <FilledRow label="Adresa úmrtí" value={vstupni.adresaUmrti} />
          <FilledRow label="Pitevna" value={vstupni.pitevna} />
          <FilledRow label="Datum prohlídky" value={vstupni.datumProhlidky} />
          <FilledRow label="Čas prohlídky" value={vstupni.casProhlidky} />
          <FilledRow label="Lékař" value={vstupni.lekarJmeno} />
          <FilledRow label="Telefon lékaře" value={vstupni.lekarTelefon} />
          <FilledRow label="Poznámka" value={vstupni.poznamka} />
        </Section>

        {hasAnyFilled(vypravitel) ? (
          <Section title="Vypravitel">
            <FilledRow label="Jméno" value={vypravitel.jmeno} />
            <FilledRow label="Příjmení" value={vypravitel.prijmeni} />
            <FilledRow label="Datum narození" value={vypravitel.datumNarozeni} />
            <FilledRow label="Telefon" value={vypravitel.telefon} />
            <FilledRow label="E-mail" value={vypravitel.email} />
            <FilledRow label="Vztah" value={vypravitel.vztah} />
            <FilledRow label="Město" value={vypravitel.mesto} />
            <FilledRow label="PSČ" value={vypravitel.psc} />
            <FilledRow label="Adresa" value={vypravitel.adresa} />
            <FilledRow label="Poznámka" value={vypravitel.poznamka} />
          </Section>
        ) : null}

        {hasAnyFilled(zesnula) ? (
          <Section title="Zesnulá osoba">
            <FilledRow label="Jméno" value={zesnula.jmeno} />
            <FilledRow label="Příjmení" value={zesnula.prijmeni} />
            <FilledRow label="Rodné číslo" value={zesnula.rodneCislo} />
            <FilledRow label="Datum narození" value={zesnula.datumNarozeni} />
            <FilledRow label="Datum úmrtí" value={zesnula.datumUmrti} />
            <FilledRow label="Čas úmrtí" value={zesnula.casUmrti} />
            <FilledRow label="Místo úmrtí" value={zesnula.mistoUmrti} />
            <FilledRow label="Adresa úmrtí" value={zesnula.adresaUmrti} />
            <FilledRow label="Trvalé bydliště" value={zesnula.trvaleBydliste} />
            <FilledRow label="Národnost" value={zesnula.narodnost} />
            <FilledRow label="Stav" value={zesnula.stav} />
            <FilledRow label="Poznámka" value={zesnula.poznamka} />
          </Section>
        ) : null}

        {hasAnyFilled(prevoz) ? (
          <Section title="Převoz">
            <FilledRow label="Typ převozu" value={prevoz.typPrevozu} />
            <FilledRow label="Místo odvozu" value={prevoz.mistoOdvozu} />
            <FilledRow label="Adresa odvozu" value={prevoz.adresaOdvozu} />
            <FilledRow label="Cíl převozu" value={prevoz.cilPrevozuTyp} />
            <FilledRow label="Název cíle" value={prevoz.cilPrevozuNazev} />
            <FilledRow label="Adresa cíle" value={prevoz.cilPrevozuAdresa} />
            <FilledRow label="Datum převozu" value={prevoz.datumPrevozu} />
            <FilledRow label="Čas převozu" value={prevoz.casPrevozu} />
            <FilledRow label="Posádka" value={prevoz.posadka} />
            <FilledRow label="Vozidlo" value={prevoz.vozidlo} />
            <FilledRow label="Počet km" value={prevoz.km} />
            <FilledRow label="Navazující rodinný převoz" value={prevoz.navazujiciRodinnyPrevoz ? "Ano" : "Ne"} />
            <FilledRow label="Pitva uzavřena" value={prevoz.pitvaUzavrena ? "Ano" : "Ne"} />
            <FilledRow label="Primární plátce výjezdu" value={zaklad?.vyjezdPlatce || vyjezd?.vyjezdPlatce} />
            <FilledRow label="Hlavní fakturační větev" value={hlavniFakturacniVetevLabel} />
            <FilledRow label="Aktivní PČR větev" value={aktivniPcrVetev ? "Ano" : "Ne"} />
            <FilledRow label="Aktivní pojišťovna větev" value={aktivniPojistovnaVetev ? "Ano" : "Ne"} />
            <FilledRow label="Aktivní rodinná větev" value={aktivniRodinnaVetev ? "Ano" : "Ne"} />
            <FilledRow label="PČR km" value={prevoz?.kmRozpad?.pcrKm} />
            <FilledRow label="Pojišťovna km" value={prevoz?.kmRozpad?.pojistovnaKm} />
            <FilledRow label="Rodina km" value={prevoz?.kmRozpad?.rodinaKm} />
            <FilledRow label="Pojišťovna km výpočet" value={prevoz?.kmRozpad?.pojistovnaKmVypocet} />
            <FilledRow label="Poznámka" value={prevoz.poznamka} />
          </Section>
        ) : null}

        {maRodinnouCast ? (
          <Section title={isKremace ? "Kremace" : isPohreb ? "Pohřeb" : "Kremace / Pohřeb"}>
            <FilledRow label="Typ" value={obrad.typ} />
            <FilledRow label="Datum obřadu" value={obrad.datum} />
            <FilledRow label="Čas obřadu" value={obrad.cas} />
            <FilledRow label="Místo" value={obrad.misto} />
            <FilledRow label="Adresa místa" value={obrad.mistoAdresa} />
            <FilledRow label="ID místa" value={obrad.mistoId} />
            <FilledRow label="Skupina místa" value={obrad.mistoSkupina} />
            <FilledRow label="Varianta rozloučení" value={obrad.ceremonyVariant} />
            <FilledRow label="Typ kostelního rozloučení" value={obrad.churchServiceType} />
            <FilledRow label="Vedoucí obřadu" value={renderStaffSummary(vedouciObradu)} />
            <FilledRow label="Obřadní služba / druhý člověk" value={renderStaffSummary(obradniSluzba)} />
            <FilledRow label="Řečník" value={renderSpeakerSummary(obradSummary?.recnikTyp || recnikOsoba)} />
            <FilledRow label="Hudba" value={renderMusicTypeSummary(obradSummary?.hudbaTyp)} />
            <FilledRow label="Hudbu zajišťuje" value={renderMusicPersonSummary(obradSummary?.hudbaZajistuje || hudbaOsoba)} />
            <FilledRow label="Farář" value={obradSummary?.fararJmeno} />
            <FilledRow label="Auto pro obřad" value={renderVehicleSummary(hlavniVuz)} />
            <FilledRow label="Poznámka" value={obradSummary?.poznamka} />
            {isKremace ? <FilledRow label="Krematorium" value={obrad.krematorium} /> : null}
            {isPohreb ? <FilledRow label="Hřbitov" value={obrad.hrbitov} /> : null}
          </Section>
        ) : null}

        {maRodinnouCast ? (
          <Section title="Květiny">
            {hasKvetiny ? (
              <div>
                <div style={{ marginBottom: 12, display: "grid", gap: 6 }}>
                  <div>
                    <strong>Stav květinové objednávky:</strong>{" "}
                    {kvetinyItems.some((x) => x?.stavObjednavky === "doobjednano")
                      ? "Doobjednáno"
                      : kvetinyItems.some((x) => x?.objednano)
                        ? "Objednáno"
                        : "Neobjednáno"}
                  </div>
                  <div>
                    <strong>Kopie objednateli:</strong>{" "}
                    {kvetiny?.copyToCustomer ? "Ano" : "Ne"}
                  </div>
                </div>
                {kvetinyItems.map((item, index) => {
                const itemCenaZaKusSDph = Number(item?.cenaSDph || 0) || 0;
                const itemMnozstvi = Number(item?.mnozstvi || 1) || 1;
                const itemCelkem = round2(itemCenaZaKusSDph * itemMnozstvi);
                const itemDphPct = Number(item?.dph || 0) || 0;
                const itemBez =
                  itemDphPct > 0 ? round2(itemCelkem / (1 + itemDphPct / 100)) : round2(itemCelkem);
                const itemDphCastka = round2(itemCelkem - itemBez);

                  return (
                    <div
                      key={`kvetina-final-${index}`}
                      style={{
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: 12,
                        background: "#f9fafb",
                      }}
                    >
                      <div style={{ fontSize: 16, fontWeight: 900, color: "#111827", marginBottom: 10 }}>
                        Květina {index + 1}
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                        <div><strong>Objednáno:</strong> {item?.objednano ? "Ano" : "Ne"}</div>
                        <div>
                            <strong>Stav objednávky:</strong>{" "}
                            {item?.stavObjednavky === "doobjednano"
                              ? "Doobjednáno"
                              : item?.objednano
                                ? "Objednáno"
                                : "Neobjednáno"}
                          </div>
                          <div><strong>Datum objednání:</strong> {renderSummaryValue(item?.datumObjednani)}</div>
                          <div><strong>Čas objednání:</strong> {renderSummaryValue(item?.casObjednani)}</div>
                          <div><strong>Dodavatel:</strong> {renderSummaryValue(item?.dodavatel)}</div>
                          <div><strong>Kopie objednateli:</strong> {kvetiny?.copyToCustomer ? "Ano" : "Ne"}</div>
                        <div><strong>Typ vazby:</strong> {renderSummaryValue(item?.typVazby)}</div>
                        <div><strong>Barva květin:</strong> {renderSummaryValue(item?.barva)}</div>
                        <div><strong>Barva stuhy:</strong> {renderSummaryValue(item?.stuhaBarva)}</div>
  
                        <div><strong>Text stuha 1:</strong> {renderSummaryValue(item?.stuhaText1)}</div>
                        <div><strong>Text stuha 2:</strong> {renderSummaryValue(item?.stuhaText2)}</div>
                        <div><strong>Poznámka:</strong> {renderSummaryValue(item?.poznamka)}</div>
                          <div>
                            <strong>Počet odeslání:</strong>{" "}
                            {Array.isArray(item?.emailLog) ? item.emailLog.length : 0}
                          </div>
                          {index === 0 ? (
                            <>
                              <div>
                                <strong>Celkem odeslání za zakázku:</strong>{" "}
                                {kvetinyOrderHistory.length}
                              </div>
                              <div>
                                <strong>Poslední odeslání květin:</strong>{" "}
                                {lastKvetinyOrderLog?.sentAt
                                  ? renderSummaryValue(lastKvetinyOrderLog.sentAt)
                                  : "—"}
                              </div>
                              <div>
                                <strong>Poslední režim:</strong>{" "}
                                {lastKvetinyOrderLog?.mode === "doobjednani"
                                  ? "Doobjednávka"
                                  : lastKvetinyOrderLog?.mode === "prvni_objednani"
                                    ? "První objednávka"
                                    : "—"}
                              </div>
                              <div>
                                <strong>Poslední komu:</strong>{" "}
                                {renderSummaryValue(lastKvetinyOrderLog?.to)}
                              </div>
                              <div>
                                <strong>Poslední kopie:</strong>{" "}
                                {renderSummaryValue(lastKvetinyOrderLog?.cc)}
                              </div>
                            </>
                          ) : null}
                      </div>

                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                        <span style={softBadgeStyle}>Cena za 1 ks s DPH: {formatMoney(itemCenaZaKusSDph)}</span>
                        <span style={softBadgeStyle}>Bez DPH celkem: {formatMoney(itemBez)}</span>
                        <span style={softBadgeStyle}>DPH {itemDphPct} %: {formatMoney(itemDphCastka)}</span>
                        <span style={softBadgeStyle}>Celkem: {formatMoney(itemCelkem)}</span>
                      </div>
                    </div>
                  );
                })}

                <div
                  style={{
                    border: "1px solid #dbeafe",
                    borderRadius: 12,
                    padding: 12,
                    background: "#eff6ff",
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 900, color: "#1e3a8a", marginBottom: 8 }}>
                    Součet květin
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 14, color: "#374151" }}>
                      Bez DPH: <strong>{formatMoney(kvetinyTotals.bez)}</strong>
                    </div>
                    <div style={{ fontSize: 14, color: "#374151" }}>
                      DPH: <strong>{formatMoney(kvetinyTotals.dph)}</strong>
                    </div>
                    <div style={{ fontSize: 15, color: "#111827", fontWeight: 900 }}>
                      Celkem: {formatMoney(kvetinyTotals.celkem)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: "#6b7280" }}>Květiny nejsou u této zakázky vyplněny.</div>
            )}
          </Section>
        ) : null}

            <Section title="Fotky a dokumenty">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                }}
              >
                {[
                  { label: "Poslední kategorie", value: fotky?.lastCategory || "—" },
                  { label: "Celkem souborů", value: String(photoCount) },
                  { label: "Ohledací list", value: hasOhledaciList ? "Ano" : "Ne" },
                  { label: "OP zesnulého", value: opZesnulyCount > 0 ? `${opZesnulyCount} soubor(ů)` : "Ne" },
                  { label: "OP objednavatele", value: opObjednavatelCount > 0 ? `${opObjednavatelCount} soubor(ů)` : "Ne" },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      padding: 12,
                      background: "#f9fafb",
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", marginBottom: 6 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          {maRodinnouCast ? (
            <Section title="Výběr služeb">
          {dopravaMode ? (
            <>
              <div style={{ marginBottom: 10, fontSize: 14, color: "#374151" }}>
                Režim dopravy: <strong>{getDopravaModeLabel(dopravaMode)}</strong>
              </div>
              <div style={{ marginBottom: 12, fontSize: 14, color: "#111827", fontWeight: 700 }}>
                {getDopravaSummary(dopravaMode, etapyKmCelkem)}
              </div>
            </>
          ) : null}

            {sluzbyRows.length ? (
              <div style={{ display: "grid", gap: 14 }}>
                {groupedServiceRows.map((group) => (
                  <div
                    key={`group-${group.key}`}
                    style={{
                      border: "1px solid #d1d5db",
                      borderRadius: 14,
                      padding: 12,
                      background: "#f9fafb",
                    }}
                  >
                    <div style={{ fontSize: 16, fontWeight: 900, color: "#111827", marginBottom: 10 }}>
                      {group.title}
                    </div>

                    <div style={{ display: "grid", gap: 10 }}>
                      {group.rows.map((row) => (
                        <div
                          key={`sluzba-${group.key}-${row.id}`}
                          style={{
                            border: row.suspiciousPrice ? "1px solid #fecaca" : "1px solid #e5e7eb",
                            borderRadius: 12,
                            padding: 12,
                            background: row.suspiciousPrice ? "#fff7f7" : "#fff",
                          }}
                        >
                          <div style={{ fontSize: 15, fontWeight: 900, color: "#111827", marginBottom: 8 }}>
                            {row.nazev}
                          </div>
                          <div style={{ display: "grid", gap: 6 }}>
                            <div style={{ fontSize: 14, color: "#374151" }}>
                              Množství: <strong>{row.mnozstvi}</strong> {row.jednotka}
                            </div>
                            <div style={{ fontSize: 14, color: "#374151" }}>
                              Cena bez DPH / jednotka: <strong>{formatMoney(row.cenaBezDph)}</strong>
                            </div>
                            <div style={{ fontSize: 14, color: "#374151" }}>
                              DPH: <strong>{row.dphPct} %</strong>
                            </div>
                            <div style={{ fontSize: 14, color: "#374151" }}>
                              Řádek bez DPH: <strong>{formatMoney(row.bez)}</strong>
                            </div>
                            <div style={{ fontSize: 14, color: "#374151" }}>
                              Řádek DPH: <strong>{formatMoney(row.dph)}</strong>
                            </div>
                            <div style={{ fontSize: 15, color: "#111827", fontWeight: 900 }}>
                              Řádek celkem: {formatMoney(row.celkem)}
                            </div>
                          </div>
                        </div>
                      ))}

                      <div
                        style={{
                          border: "1px solid #dbeafe",
                          borderRadius: 12,
                          padding: 12,
                          background: "#eff6ff",
                        }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 900, color: "#1e3a8a", marginBottom: 8 }}>
                          Součet bloku
                        </div>
                        <div style={{ display: "grid", gap: 6 }}>
                          <div style={{ fontSize: 14, color: "#374151" }}>
                            Bez DPH: <strong>{formatMoney(group.totals.bez)}</strong>
                          </div>
                          <div style={{ fontSize: 14, color: "#374151" }}>
                            DPH: <strong>{formatMoney(group.totals.dph)}</strong>
                          </div>
                          <div style={{ fontSize: 15, color: "#111827", fontWeight: 900 }}>
                            Celkem: {formatMoney(group.totals.celkem)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 12,
                    padding: 12,
                    background: "#f3f4f6",
                  }}
                >
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#111827", marginBottom: 8 }}>
                    Celkový součet služeb
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 14, color: "#374151" }}>
                      Bez DPH: <strong>{formatMoney(finalTotals.bez)}</strong>
                    </div>
                    <div style={{ fontSize: 14, color: "#374151" }}>
                      DPH: <strong>{formatMoney(finalTotals.dph)}</strong>
                    </div>
                    <div style={{ fontSize: 16, color: "#111827", fontWeight: 900 }}>
                      Celkem: {formatMoney(finalTotals.celkem)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: "#6b7280", fontWeight: 700 }}>Zatím nejsou vybrané žádné služby.</div>
            )}
        </Section>
        ) : null}

          
      </div>
    </div>
  );
}
